import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { getDocument, getDocuments, getQAResult, getPdfUrl, getRenderUrl, submitReviewerDecision, completeReview, closeDocument, reopenDocument, abortDocument, restoreDocument, getWaveformAnalysis, getWaveformQa, getMsoStatusBulk, rerunDocumentQA, getPidTrackers, clearDocumentsCache, addTechFlag, resolveTechFlag, unlockReview, saveWaveformQa, addToWaveformSelection, removeFromWaveformSelection } from "../lib/api";
import { ScoreRing, StatusBadge } from "../components/SharedComponents";
import { useMsal } from "@azure/msal-react";
import MSOTab from '../components/mso/MSOTab';
import IDRTab from '../components/idr/IDRTab';
import WaveformQATab from '../components/waveform/WaveformQATab';
import { useRoles } from "../lib/useRoles";

// Source label metadata for multi-source evidence sections
const SOURCE_LABEL_META = {
  CHAT:   { label: "Chat Log",        bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
  SCREEN: { label: "Screenshot",      bg: "#fffbeb", color: "#92400e", border: "#fde68a" },
  TECH:   { label: "Tech Report",     bg: "#ecfeff", color: "#0e7490", border: "#a5f3fc" },
  IP:     { label: "Interp. Report",  bg: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
};

// Inner helper: renders inline [SCREENSHOT:pN] tokens as images within a text run
function renderScreenshotTokens(text, screenshotFileName) {
  if (!text) return null;
  if (!screenshotFileName) return text;
  const tokenRegex = /\[SCREENSHOT:p(\d+)\]/g;
  const parts = [];
  let lastIdx = 0;
  let match;
  let k = 0;
  while ((match = tokenRegex.exec(text)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<span key={`t-${k++}`}>{text.slice(lastIdx, match.index)}</span>);
    }
    const pageNum = match[1];
    const imgUrl = getRenderUrl(screenshotFileName, pageNum);
    parts.push(
      <div key={`img-${k++}`} style={{ margin: "8px 0", border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden", maxWidth: 480 }}>
        <img
          src={imgUrl}
          alt={`Screenshot page ${pageNum}`}
          style={{ width: "100%", display: "block", cursor: "zoom-in" }}
          onClick={() => window.open(imgUrl, "_blank")}
          loading="lazy"
        />
        <div style={{ padding: "4px 8px", fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--text-tertiary)", background: "#f9fafb" }}>
          Screenshot · page {pageNum} · click to enlarge
        </div>
      </div>
    );
    lastIdx = tokenRegex.lastIndex;
  }
  if (lastIdx < text.length) {
    parts.push(<span key={`t-${k++}`}>{text.slice(lastIdx)}</span>);
  }
  return parts.length > 0 ? <>{parts}</> : text;
}

// Outer helper: parses [CHAT]/[SCREEN]/[TECH]/[IP] section labels, falls back
// to inline screenshot tokens when no labels are present.
function renderEvidenceWithScreenshots(evidence, screenshotFileName) {
  if (!evidence) return "—";
  const labelRegex = /\[(CHAT|SCREEN|TECH|IP)\]/g;
  const matches = [];
  let m;
  while ((m = labelRegex.exec(evidence)) !== null) {
    matches.push({ tag: m[1], index: m.index, length: m[0].length });
  }
  // No labels → fall back to plain screenshot-token rendering (single-source checks)
  if (matches.length === 0) {
    return renderScreenshotTokens(evidence, screenshotFileName);
  }
  // Slice evidence into one section per label
  const sections = matches.map((mm, i) => {
    const start = mm.index + mm.length;
    const end = i + 1 < matches.length ? matches[i + 1].index : evidence.length;
    return { tag: mm.tag, content: evidence.slice(start, end).trim() };
  }).filter(s => s.content.length > 0);
  return (
    <>
      {sections.map((s, idx) => {
        const meta = SOURCE_LABEL_META[s.tag] || SOURCE_LABEL_META.CHAT;
        return (
          <div key={`sec-${idx}`} style={{ marginBottom: idx < sections.length - 1 ? 10 : 0 }}>
            <span style={{
              display: "inline-block",
              fontSize: 9,
              fontFamily: "var(--font-mono)",
              fontWeight: 600,
              padding: "2px 8px",
              borderRadius: 4,
              background: meta.bg,
              color: meta.color,
              border: `1px solid ${meta.border}`,
              marginBottom: 4,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}>
              {meta.label}
            </span>
            <div>{renderScreenshotTokens(s.content, screenshotFileName)}</div>
          </div>
        );
      })}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ICONS
   ═══════════════════════════════════════════════════════════════════ */
const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 8 6.5 11.5 13 5"/></svg>
);
const XIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round"><line x1="4" y1="4" x2="12" y2="12"/><line x1="12" y1="4" x2="4" y2="12"/></svg>
);
const QuestionIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round"><circle cx="8" cy="8" r="6"/><path d="M6.5 6a1.5 1.5 0 013 0c0 1-1.5 1.5-1.5 2.5"/><circle cx="8" cy="12" r=".75" fill="#d97706" stroke="none"/></svg>
);
const NAIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><line x1="4" y1="8" x2="12" y2="8"/></svg>
);
const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    style={{ transition:"transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}>
    <path d="M4 6l4 4 4-4"/>
  </svg>
);

const RESULT_STYLE = {
  PASSED:         { icon: <CheckIcon/>,    color:"#16a34a", bg:"#f0fdf4", border:"#bbf7d0", label:"PASS" },
  FAILED:         { icon: <XIcon/>,        color:"#dc2626", bg:"#fef2f2", border:"#fecaca", label:"FAIL" },
  UNDETERMINED:   { icon: <QuestionIcon/>, color:"#d97706", bg:"#fffbeb", border:"#fde68a", label:"UTD" },
  NOT_APPLICABLE: { icon: <NAIcon/>,       color:"#94a3b8", bg:"#f8fafc", border:"#e2e8f0", label:"N/A" },
};


/* ═══════════════════════════════════════════════════════════════════
   SOURCE BADGES + BANNER (for PID_QA multi-file cases)
   ═══════════════════════════════════════════════════════════════════ */
const SOURCE_BADGE_STYLE = {
  "Usmon Chat Log":         { short:"CHAT",  color:"#2563eb", bg:"#eff6ff", border:"#bfdbfe" },
  "Chat Log and Screenshots":{ short:"CHAT",  color:"#2563eb", bg:"#eff6ff", border:"#bfdbfe" },
  "Interpretation Report":  { short:"IP",    color:"#7c3aed", bg:"#f5f3ff", border:"#ddd6fe" },
  "Technical Report":       { short:"TECH",  color:"#0891b2", bg:"#ecfeff", border:"#a5f3fc" },
  "Usmon Screenshots":      { short:"SCRN",  color:"#d97706", bg:"#fffbeb", border:"#fde68a" },
};

// Aliases so legacy CSV values still resolve to the canonical SOURCE_BADGE_STYLE keys
const SOURCE_ALIAS_MAP = {
  "event log":   "Usmon Chat Log",
  "chat log":    "Usmon Chat Log",
  "screenshots": "Usmon Screenshots",
};

function resolveSourceKey(rawPart) {
  const trimmed = (rawPart || "").trim();
  if (!trimmed) return null;
  if (SOURCE_BADGE_STYLE[trimmed]) return trimmed;
  const alias = SOURCE_ALIAS_MAP[trimmed.toLowerCase()];
  if (alias && SOURCE_BADGE_STYLE[alias]) return alias;
  return trimmed; // unknown — render as-is
}

function SourceBadge({ requiredSources, availableSources }) {
  if (!requiredSources) return null;

  // Parse delimiter: "/" is the new format, " and " is legacy
  const rawParts = requiredSources.includes("/")
    ? requiredSources.split("/")
    : requiredSources.split(" and ");
  const parts = rawParts.map(resolveSourceKey).filter(Boolean);
  if (parts.length === 0) return null;

  // Comparison checks (ALL semantics): "Interpretation Report" + another source via "/"
  // These checks compare two documents and require BOTH to evaluate.
  const isComparison =
    requiredSources.includes("/") &&
    parts.includes("Interpretation Report") &&
    parts.length > 1 &&
    parts.some(p => p !== "Interpretation Report");

  return (
    <span style={{ display:"inline-flex", gap:3, verticalAlign:"middle" }}>
      {parts.map((part, i) => {
        const style = SOURCE_BADGE_STYLE[part] || { short: part.slice(0,4).toUpperCase(), color:"#94a3b8", bg:"#f8fafc", border:"#e2e8f0" };
        const available = availableSources.has(part);

        let pillBg, pillColor, pillBorder, suffix, tooltip;
        if (available) {
          // Green: uploaded and used in this check
          pillBg = style.bg;
          pillColor = style.color;
          pillBorder = style.border;
          suffix = null;
          tooltip = `${part} — uploaded`;
        } else if (isComparison) {
          // Red: comparison check requires this source but it's missing → check could not run
          pillBg = "#fef2f2";
          pillColor = "#dc2626";
          pillBorder = "#fecaca";
          suffix = "✗";
          tooltip = `${part} — required for comparison but not uploaded`;
        } else {
          // Grey: ANY-semantics check ran without this optional source
          pillBg = "#f8fafc";
          pillColor = "#94a3b8";
          pillBorder = "#e2e8f0";
          suffix = "—";
          tooltip = `${part} — not uploaded (check ran without it)`;
        }

        return (
          <span key={`${part}-${i}`} title={tooltip} style={{
            fontSize:9, fontFamily:"var(--font-mono)", fontWeight:600, padding:"1px 5px", borderRadius:3,
            background:pillBg, color:pillColor, border:`1px solid ${pillBorder}`, letterSpacing:"0.03em",
            display:"inline-flex", alignItems:"center", gap:2,
          }}>
            {suffix && <span style={{ fontSize:8, opacity:0.85 }}>{suffix}</span>}
            {style.short}
          </span>
        );
      })}
    </span>
  );
}

function SourceSummaryBanner({ availableSources }) {
  const ALL_EXPECTED = [
    { key: "Usmon Chat Log",        short: "CHAT", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
    { key: "Interpretation Report", short: "IP",   color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
    { key: "Technical Report",      short: "TECH", color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
    { key: "Usmon Screenshots",     short: "SCRN", color: "#d97706", bg: "#fffbeb", border: "#fde68a" },
  ];
  const uploaded = ALL_EXPECTED.filter(s => availableSources.has(s.key));
  const missing  = ALL_EXPECTED.filter(s => !availableSources.has(s.key));

  return (
    <div className="card" style={{ marginBottom:12, padding:"12px 16px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
        <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginRight:4 }}>Sources:</span>
        {uploaded.map(s => (
          <span key={s.key} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:5,
            background:s.bg, border:`1px solid ${s.border}`, fontSize:10, fontFamily:"var(--font-mono)", fontWeight:600, color:s.color }}>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke={s.color} strokeWidth="2.5" strokeLinecap="round"><polyline points="3 8 6.5 11.5 13 5"/></svg>
            {s.short}
          </span>
        ))}
        {missing.map(s => (
          <span key={s.key} style={{ display:"inline-flex", alignItems:"center", gap:4, padding:"3px 8px", borderRadius:5,
            background:"#fffbeb", border:"1px solid #fde68a", fontSize:10, fontFamily:"var(--font-mono)", fontWeight:500, color:"#92400e" }}>
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round"><path d="M8 3v6"/><circle cx="8" cy="12" r="0.75" fill="#d97706" stroke="none"/></svg>
            {s.short}
          </span>
        ))}
        <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", marginLeft:4 }}>
          {uploaded.length} of {ALL_EXPECTED.length} sources
        </span>
      </div>
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════════════
   AUDIT TYPE TOGGLE — same style as Documents page
   ═══════════════════════════════════════════════════════════════════ */
const AUDIT_TYPES = [
  { key: "Both",         label: "Both" },
  { key: "Technologist", label: "Tech",             color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  { key: "IP",           label: "Interp. Physician", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
];

function AuditTypeToggle({ value, onChange }) {
  return (
    <div style={{ display:"inline-flex", borderRadius:7, border:"1px solid var(--border)", overflow:"hidden" }}>
      {AUDIT_TYPES.map((at, i) => {
        const active = value === at.key;
        const accentBg    = active && at.bg    ? at.bg    : active ? "#eff6ff" : "var(--white)";
        const accentColor = active && at.color ? at.color : active ? "#1d4ed8" : "var(--text-secondary)";
        return (
          <button key={at.key} onClick={() => onChange(at.key)} style={{
            padding:"5px 12px", fontSize:11, fontWeight: active ? 600 : 400,
            fontFamily:"var(--font-mono)", cursor:"pointer", border:"none", outline:"none",
            background: accentBg, color: accentColor, position:"relative",
            borderRight: i < AUDIT_TYPES.length - 1 ? "1px solid var(--border)" : "none",
            transition:"all 0.15s",
          }}>
            {at.label}
            {active && <div style={{ position:"absolute", bottom:0, left:"15%", right:"15%", height:2, borderRadius:1, background: accentColor }}/>}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   QA CHECK ROW
   ═══════════════════════════════════════════════════════════════════ */
/* ── Override reason presets ── */
const OVERRIDE_REASONS = {
  PASS_TO_FAIL: [
    "AI missed a documentation gap",
    "Evidence does not support a pass",
    "Incorrect source was evaluated",
    "Criteria misinterpreted by AI",
    "Other",
  ],
  FAIL_TO_PASS: [
    "Evidence exists in another source file",
    "AI misinterpreted the documentation",
    "Check criteria does not apply to this case",
    "Documentation is sufficient upon manual review",
    "Other",
  ],
};

/* ── Override badge icon ── */
const OverrideIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M1 4v6h6"/><path d="M3.51 10a7 7 0 0112.13-3"/>
  </svg>
);

function QARow({ rule, docId, reviewerName, isDocReviewed, onDecisionSaved, onFlagSaved, onViewWaveform }) {
  // Flags are the one write a technologist has on a case. isTech here is the
  // effective role, so an admin viewing as a tech sees what that tech sees;
  // the backend still records the real signed-in user as the flagger.
  const { isTech, isIP } = useRoles();
  // Reviewer names are withheld from both: neither is part of the review.
  const hideReviewer = isTech || isIP;
  // Raising a flag is the one write either of them has. Resolving one is a
  // reviewer action, so neither gets it.
  const canFlag = isTech || isIP;
  const [flag, setFlag]           = useState(rule.techFlag || null);
  const [flagForm, setFlagForm]   = useState(null);   // "raise" | "resolve" | null
  const [flagNote, setFlagNote]   = useState("");
  const [flagSaving, setFlagSaving] = useState(false);
  const flagOpen = !!flag && flag.open !== false;

  const submitFlag = async () => {
    const note = flagNote.trim();
    if (!note || flagSaving) return;
    setFlagSaving(true);
    try {
      await addTechFlag(docId, rule.ruleId, note, reviewerName);
      const raised = { note, by: reviewerName, at: new Date().toISOString(), open: true };
      setFlag(raised);
      // The banner above and the case grid both count flags, so tell the page
      // and drop the cached document list rather than waiting for a reload.
      onFlagSaved?.(rule.ruleId, raised);
      clearDocumentsCache();
      setFlagForm(null); setFlagNote("");
    } catch (e) { alert("Could not save the flag: " + (e?.message || String(e))); }
    setFlagSaving(false);
  };

  const submitResolve = async () => {
    if (flagSaving) return;
    if (resolveAction === "override" && !resolveReason) {
      alert("Please select a reason for changing the result.");
      return;
    }
    setFlagSaving(true);
    try {
      const note = flagNote.trim();
      if (resolveAction === "override") {
        // Same decision row the reviewer would have written before sign-off,
        // so the score and the audit trail treat it like any other override.
        const fullComment = `[${resolveReason}]${note ? " " + note : ""} (raised by technologist flag)`;
        await submitReviewerDecision(docId, rule.ruleId, flipTo, fullComment, reviewerName);
        setDecision(flipTo);
        setSaved(true);
        setShowOverrideForm(false);
        onDecisionSaved(rule.ruleId, flipTo);
      }
      const outcome = resolveAction === "override" ? flipLabel : "Result unchanged";
      const resolution = note ? `${outcome}: ${note}` : outcome;
      await resolveTechFlag(docId, rule.ruleId, resolution, reviewerName);
      const answered = { ...(flag || {}), open: false, resolvedBy: reviewerName,
                         resolvedAt: new Date().toISOString(), resolutionNote: resolution };
      setFlag(answered);
      onFlagSaved?.(rule.ruleId, answered);
      clearDocumentsCache();
      setFlagForm(null); setFlagNote(""); setResolveAction("uphold"); setResolveReason("");
    } catch (e) { alert("Could not resolve the flag: " + (e?.message || String(e))); }
    setFlagSaving(false);
  };

  const isFail = rule.status === "FAILED";
  const isPass = rule.status === "PASSED";
  const isNA   = rule.status === "NOT_APPLICABLE";

  const [open, setOpen]             = useState(isFail && !rule.reviewerDecision);
  const [decision, setDecision]     = useState(rule.reviewerDecision || null);
  const [comment, setComment]       = useState(rule.reviewerComment || "");
  const [overrideReason, setOverrideReason] = useState(rule.reviewerReason || "");
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(!!rule.reviewerDecision);
  const [showOverrideForm, setShowOverrideForm] = useState(false);

  // Answering a flag is two things at once: what happens to the check, and
  // what the technologist is told. A reviewer can uphold the result or flip
  // it; either way the flag closes and the reply is recorded.
  const [resolveAction, setResolveAction] = useState("uphold");
  const [resolveReason, setResolveReason] = useState("");

  // What the check reads as right now, including an override already saved.
  const effNow = decision === "OVERRIDE_PASS" ? "PASSED"
               : decision === "OVERRIDE_FAIL" ? "FAILED"
               : (rule.effectiveStatus || rule.status);
  const flipTo    = effNow === "PASSED" ? "OVERRIDE_FAIL" : "OVERRIDE_PASS";
  const flipLabel = effNow === "PASSED" ? "Override to FAIL" : "Override to PASS";
  const flipReasons = effNow === "PASSED" ? OVERRIDE_REASONS.PASS_TO_FAIL : OVERRIDE_REASONS.FAIL_TO_PASS;

  const rs = RESULT_STYLE[rule.status] || RESULT_STYLE.UNDETERMINED;

  const isOverride = decision === "OVERRIDE_PASS" || decision === "OVERRIDE_FAIL";
  const isOverrideToPass = decision === "OVERRIDE_PASS";
  const isOverrideToFail = decision === "OVERRIDE_FAIL";

  const handleDecision = (d) => {
    if (isDocReviewed || saving || saved) return;
    setDecision(d);
    if (d === "OVERRIDE_PASS" || d === "OVERRIDE_FAIL") {
      setShowOverrideForm(true);
    } else {
      setShowOverrideForm(false);
    }
  };

  const handleSave = async () => {
    if (!decision || saving || isDocReviewed) return;
    // Require reason for overrides
    if ((decision === "OVERRIDE_PASS" || decision === "OVERRIDE_FAIL") && !overrideReason) {
      alert("Please select a reason for the override.");
      return;
    }
    setSaving(true);
    try {
      // Combine reason + comment for the audit trail
      const fullComment = overrideReason
        ? `[${overrideReason}]${comment ? " " + comment : ""}`
        : comment;
      await submitReviewerDecision(docId, rule.ruleId, decision, fullComment, reviewerName);
      setSaved(true);
      setShowOverrideForm(false);
      onDecisionSaved(rule.ruleId, decision);
    } catch (e) { alert("Failed to save: " + (e?.message || String(e))); }
    setSaving(false);
  };

  // Determine display state
  const getDisplayState = () => {
    if (!saved || !decision) return null;
    if (decision === "OVERRIDE_PASS") return { label: "OVERRIDDEN TO PASS", bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", iconColor: "#1d4ed8" };
    if (decision === "OVERRIDE_FAIL") return { label: "OVERRIDDEN TO FAIL", bg: "#fffbeb", color: "#92400e", border: "#fde68a", iconColor: "#d97706" };
    if (decision === "CONFIRMED_FAIL") return { label: "CONFIRMED", bg: "#fef2f2", color: "#dc2626", border: "#fecaca", iconColor: "#dc2626" };
    if (decision === "CONFIRMED_PASS") return { label: "CONFIRMED", bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0", iconColor: "#16a34a" };
    return null;
  };
  const displayState = getDisplayState();

  return (
    <div style={{ borderBottom:"1px solid var(--border)", opacity: isNA ? 0.55 : 1 }}>
      {/* ── Row header ── */}
      <div onClick={() => !isNA && setOpen(o => !o)} style={{
        padding:"10px 16px", display:"flex", alignItems:"center", gap:10, cursor: isNA ? "default" : "pointer",
        background: open ? rs.bg : "transparent", transition:"background 0.1s" }}>

        {/* Icon — changes to override icon when overridden */}
        <div style={{ width:24, height:24, borderRadius:5,
          background: displayState && isOverride ? displayState.bg : rs.bg,
          border: `1px solid ${displayState && isOverride ? displayState.border : rs.border}`,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
          color: displayState && isOverride ? displayState.iconColor : rs.color }}>
          {saved && isOverride ? <OverrideIcon/> : rs.icon}
        </div>

        {/* Check name + badges */}
        <div style={{ flex:1, minWidth:0 }}>
          <span style={{ fontSize:12.5, fontWeight:500, color: isNA ? "var(--text-tertiary)" : "var(--text-primary)" }}>{rule.description}</span>
          <span style={{ marginLeft:8, fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>{rule.ruleId}</span>
          {rule.requiredSources && <span style={{ marginLeft:6, display:"inline-flex", gap:3, verticalAlign:"middle" }}><SourceBadge requiredSources={rule.requiredSources} availableSources={rule._availableSources || new Set()}/></span>}
        </div>

        {/* Result badge — original + override state */}
        {saved && isOverride ? (
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <span style={{ textDecoration:"line-through", opacity:0.5, fontSize:10, fontFamily:"var(--font-mono)", color:rs.color }}>{rs.label}</span>
            <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", fontWeight:600, padding:"2px 8px", borderRadius:4,
              background:displayState.bg, color:displayState.color, border:`1px solid ${displayState.border}` }}>
              {displayState.label}
            </span>
          </div>
        ) : saved && displayState ? (
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:10.5, fontWeight:600, color:rs.color, padding:"2px 8px", borderRadius:4, background:rs.bg }}>{rs.label}</span>
            <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", fontWeight:600, padding:"2px 8px", borderRadius:4,
              background:displayState.bg, color:displayState.color, border:`1px solid ${displayState.border}` }}>
              {displayState.label}
            </span>
          </div>
        ) : (
          <span style={{ fontFamily:"var(--font-mono)", fontSize:10.5, fontWeight:600, color:rs.color, padding:"2px 8px", borderRadius:4, background:rs.bg, flexShrink:0 }}>
            {rs.label}
          </span>
        )}

        {/* Action buttons — shown inline when not yet decided */}
        {!isNA && !isDocReviewed && !saved && (
          <div style={{ display:"flex", gap:4, flexShrink:0 }} onClick={e => e.stopPropagation()}>
            {isFail && (
              <>
                <button onClick={() => handleDecision("OVERRIDE_PASS")} disabled={saving}
                  style={{ padding:"3px 8px", borderRadius:5, fontSize:10, fontFamily:"var(--font-mono)", cursor:"pointer", fontWeight:500,
                    border:`1px solid ${decision === "OVERRIDE_PASS" ? "#16a34a" : "var(--border)"}`,
                    background: decision === "OVERRIDE_PASS" ? "#f0fdf4" : "var(--white)",
                    color: decision === "OVERRIDE_PASS" ? "#16a34a" : "var(--text-secondary)" }}>
                  ✓ Override
                </button>
                <button onClick={() => handleDecision("CONFIRMED_FAIL")} disabled={saving}
                  style={{ padding:"3px 8px", borderRadius:5, fontSize:10, fontFamily:"var(--font-mono)", cursor:"pointer", fontWeight:500,
                    border:`1px solid ${decision === "CONFIRMED_FAIL" ? "#dc2626" : "var(--border)"}`,
                    background: decision === "CONFIRMED_FAIL" ? "#fef2f2" : "var(--white)",
                    color: decision === "CONFIRMED_FAIL" ? "#dc2626" : "var(--text-secondary)" }}>
                  ✗ Confirm
                </button>
              </>
            )}
            {isPass && (
              <button onClick={() => { setOpen(true); handleDecision("OVERRIDE_FAIL"); }} disabled={saving}
                style={{ padding:"3px 8px", borderRadius:5, fontSize:10, fontFamily:"var(--font-mono)", cursor:"pointer", fontWeight:500,
                  border:`1px solid ${decision === "OVERRIDE_FAIL" ? "#d97706" : "var(--border)"}`,
                  background: decision === "OVERRIDE_FAIL" ? "#fffbeb" : "var(--white)",
                  color: decision === "OVERRIDE_FAIL" ? "#d97706" : "var(--text-secondary)" }}>
                ✗ Override
              </button>
            )}
          </div>
        )}
        {/* Tech flag — a chip once raised, and the tech's own Flag control.
            Sits outside the decision buttons because it is not a decision:
            techs see their cases after sign-off, when those buttons are gone. */}
        {(flag || (canFlag && !isNA)) && (
          <div style={{ display:"flex", alignItems:"center", gap:4, flexShrink:0 }} onClick={e => e.stopPropagation()}>
            {flag && (
              <span title={flag.note || ""}
                style={{ fontSize:9.5, fontFamily:"var(--font-mono)", fontWeight:600, padding:"2px 8px", borderRadius:4,
                  background: flagOpen ? "#fffbeb" : "var(--gray-50, #f9fafb)",
                  color:      flagOpen ? "#b45309" : "var(--text-tertiary)",
                  border:`1px solid ${flagOpen ? "#fde68a" : "var(--border)"}` }}>
                {flagOpen ? "⚑ Flagged" : "\u2691 Resolved"}
              </span>
            )}
            {canFlag && !isNA && !flagOpen && (
              <button onClick={() => { setOpen(true); setFlagForm("raise"); setFlagNote(""); }}
                style={{ padding:"3px 8px", borderRadius:5, fontSize:10, fontFamily:"var(--font-mono)", cursor:"pointer",
                  fontWeight:500, border:"1px solid var(--border)", background:"var(--white)", color:"var(--text-secondary)" }}>
                ⚑ Flag
              </button>
            )}
            {!canFlag && flagOpen && (
              <button onClick={() => { setOpen(true); setFlagForm("resolve"); setFlagNote(""); }}
                style={{ padding:"3px 8px", borderRadius:5, fontSize:10, fontFamily:"var(--font-mono)", cursor:"pointer",
                  fontWeight:500, border:"1px solid #fde68a", background:"#fffbeb", color:"#b45309" }}>
                Resolve
              </button>
            )}
          </div>
        )}
        {!isNA && <ChevronIcon open={open}/>}
      </div>

      {/* ── Expanded detail panel ── */}
      {open && !isNA && (
        <div style={{ padding:"0 16px 14px 50px" }}>
          {/* What the tech asked, and what came back */}
          {flag && (
            <div style={{ marginBottom:10, padding:"8px 12px", borderRadius:6,
              border:`1px solid ${flagOpen ? "#fde68a" : "var(--border)"}`,
              background: flagOpen ? "#fffbeb" : "var(--gray-50, #f9fafb)" }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, textTransform:"uppercase",
                letterSpacing:"0.07em", color: flagOpen ? "#b45309" : "var(--text-tertiary)", marginBottom:3 }}>
                {flagOpen ? "Flagged for a reviewer" : "Flag resolved"}
              </div>
              <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>{flag.note}</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", marginTop:3 }}>
                {flag.by || "Unknown"}{flag.at ? ` \u00b7 ${new Date(flag.at).toLocaleString()}` : ""}
              </div>
              {!flagOpen && (flag.resolutionNote || flag.resolvedBy) && (
                <div style={{ marginTop:6, paddingTop:6, borderTop:"1px solid var(--border)" }}>
                  <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>
                    {flag.resolutionNote || "No note left."}
                  </div>
                  <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", marginTop:3 }}>
                    {hideReviewer ? "Reviewer" : (flag.resolvedBy || "Unknown")}{flag.resolvedAt ? ` \u00b7 ${new Date(flag.resolvedAt).toLocaleString()}` : ""}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Raise (tech) / answer (reviewer) */}
          {flagForm && (
            <div style={{ marginBottom:10, padding:14, border:"1px solid #fde68a", borderRadius:8, background:"var(--white)" }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", marginBottom:5,
                textTransform:"uppercase", letterSpacing:"0.07em" }}>
                {flagForm === "raise" ? "What should the reviewer look at? (required)" : "What happens to this check?"}
              </div>
              {flagForm === "resolve" && (
                <div style={{ marginBottom:10 }}>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {[["uphold", `Keep it as ${effNow === "PASSED" ? "PASS" : effNow === "FAILED" ? "FAIL" : "is"}`],
                      ["override", flipLabel]].map(([key, label]) => (
                      <button key={key} onClick={() => { setResolveAction(key); if (key === "uphold") setResolveReason(""); }}
                        style={{ padding:"4px 10px", borderRadius:5, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                          fontWeight:500,
                          border:`1px solid ${resolveAction === key ? (key === "override" ? "#b45309" : "#16a34a") : "var(--border)"}`,
                          background: resolveAction === key ? (key === "override" ? "#fffbeb" : "#f0fdf4") : "var(--white)",
                          color: resolveAction === key ? (key === "override" ? "#b45309" : "#16a34a") : "var(--text-secondary)" }}>
                        {label}
                      </button>
                    ))}
                  </div>
                  {resolveAction === "override" && (
                    <>
                      <select value={resolveReason} onChange={e => setResolveReason(e.target.value)}
                        style={{ width:"100%", fontSize:12, padding:"7px 10px", border:"1px solid var(--border)",
                          borderRadius:6, marginTop:8, background:"var(--white)",
                          color: resolveReason ? "var(--text-primary)" : "var(--text-tertiary)" }}>
                        <option value="">Reason for the change (required)...</option>
                        {flipReasons.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                      <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:5, lineHeight:1.4 }}>
                        This saves a normal reviewer override, so the case score changes with it.
                        The sign-off stays as it is.
                      </div>
                    </>
                  )}
                </div>
              )}
              <textarea rows={3} value={flagNote} onChange={e => setFlagNote(e.target.value)}
                placeholder={flagForm === "raise"
                  ? "Describe the concern with this check..."
                  : "Say what you changed or why the result stands..."}
                style={{ width:"100%", fontSize:12, padding:"7px 10px", borderRadius:6,
                  border:"1px solid var(--border)", resize:"vertical", fontFamily:"inherit" }}/>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:10 }}>
                <button onClick={() => { setFlagForm(null); setFlagNote(""); }}
                  style={{ padding:"4px 12px", borderRadius:5, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                    border:"1px solid var(--border)", background:"var(--white)", color:"var(--text-secondary)" }}>
                  Cancel
                </button>
                <button onClick={flagForm === "raise" ? submitFlag : submitResolve}
                  disabled={flagSaving || (flagForm === "raise" && !flagNote.trim())}
                  style={{ padding:"4px 14px", borderRadius:5, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                    fontWeight:500, border:"none", opacity: flagSaving ? 0.6 : 1,
                    background: (flagForm === "raise" && !flagNote.trim()) ? "var(--gray-200, #e5e7eb)" : "#b45309",
                    color: (flagForm === "raise" && !flagNote.trim()) ? "var(--text-tertiary)" : "#fff" }}>
                  {flagSaving ? "Saving..."
                    : flagForm === "raise" ? "Flag for reviewer"
                    : resolveAction === "override" ? `${flipLabel} & resolve`
                    : "Keep result & resolve"}
                </button>
              </div>
            </div>
          )}

          {/* AI Evidence */}
          <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>AI Evidence</div>
          <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.6, whiteSpace:"pre-wrap",
            padding:"8px 12px", background:"var(--gray-50, #f9fafb)", borderRadius:6, border:"1px solid var(--border)" }}>
            {(() => {
              const raw = rule.aiEvidence ? rule.aiEvidence.replace(/\s*\|\s*$/, "").trim() : "";
              if (!raw) return rule.status === "PASSED" ? "Check passed — criteria met." : "No evidence recorded.";
              const formatted = raw.replace(/(\d{2}:\d{2}:\d{2}\s*\|)/g, "\n$1").trim();
              return renderEvidenceWithScreenshots(formatted, rule._screenshotFileName || null);
            })()}
          </div>

          {/* Waveform link */}
          {rule.waveformRef && onViewWaveform && (
            <button onClick={() => onViewWaveform(rule.waveformRef)} style={{ marginTop:6, background:"none", border:"none", padding:0, cursor:"pointer", fontSize:11, fontFamily:"var(--font-mono)", color:"#0d9488" }}>⤢ View waveform</button>
          )}

          {/* Override form — shown for both PASS and FAIL overrides */}
          {showOverrideForm && !isDocReviewed && !saved && (
            <div style={{ marginTop:10, padding:14, border:"1px solid #bfdbfe", borderRadius:8, background:"var(--white)" }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                Reason for override (required)
              </div>
              <select value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
                style={{ width:"100%", fontSize:12, padding:"7px 10px", border:"1px solid var(--border)", borderRadius:6, marginBottom:10,
                  background:"var(--white)", color: overrideReason ? "var(--text-primary)" : "var(--text-tertiary)" }}>
                <option value="">Select a reason...</option>
                {(isFail ? OVERRIDE_REASONS.FAIL_TO_PASS : OVERRIDE_REASONS.PASS_TO_FAIL).map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.07em" }}>
                Additional details (optional)
              </div>
              <input style={{ fontSize:12, width:"100%", marginBottom:10 }}
                placeholder={`Explain why this check should ${isFail ? "pass" : "fail"}...`}
                value={comment} onChange={e => setComment(e.target.value)}/>
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                <button onClick={() => { setShowOverrideForm(false); setDecision(null); setOverrideReason(""); }}
                  style={{ padding:"4px 12px", borderRadius:5, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                    border:"1px solid var(--border)", background:"var(--white)", color:"var(--text-secondary)" }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving || !overrideReason}
                  style={{ padding:"4px 14px", borderRadius:5, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer", fontWeight:500,
                    border:"none", background: !overrideReason ? "var(--gray-200, #e5e7eb)" : isFail ? "#16a34a" : "#d97706",
                    color: !overrideReason ? "var(--text-tertiary)" : "#fff",
                    opacity: saving ? 0.6 : 1 }}>
                  {saving ? "Saving..." : `Override to ${isFail ? "PASS" : "FAIL"}`}
                </button>
              </div>
            </div>
          )}

          {/* Confirm FAIL — simple note (existing behavior) */}
          {isFail && !showOverrideForm && !isDocReviewed && !saved && decision === "CONFIRMED_FAIL" && (
            <div style={{ marginTop:10, borderTop:"1px solid var(--border)", paddingTop:10 }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", marginBottom:5, textTransform:"uppercase", letterSpacing:"0.07em" }}>Reviewer note (optional)</div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <input style={{ fontSize:12, flex:1 }} placeholder="Add a note for the audit record..." value={comment} onChange={e => setComment(e.target.value)}/>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save"}</button>
              </div>
            </div>
          )}

          {/* PASS row — show override button inside detail if not shown inline */}
          {isPass && !isDocReviewed && !saved && !showOverrideForm && decision !== "OVERRIDE_FAIL" && (
            <div style={{ marginTop:10, borderTop:"1px solid var(--border)", paddingTop:10 }}>
              <button onClick={() => handleDecision("OVERRIDE_FAIL")}
                style={{ padding:"5px 14px", borderRadius:5, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer", fontWeight:500,
                  border:"1px solid #fde68a", background:"#fffbeb", color:"#92400e" }}>
                ✗ Override to FAIL
              </button>
            </div>
          )}

          {/* Saved override justification display */}
          {saved && isOverride && (overrideReason || rule.reviewerComment || comment) && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:4 }}>Override justification</div>
              <div style={{ padding:"8px 12px", background:"var(--gray-50, #f9fafb)", borderRadius:6,
                borderLeft: `3px solid ${isOverrideToPass ? "#1d4ed8" : "#d97706"}`,
                fontSize:12, color:"var(--text-secondary)", lineHeight:1.6, fontStyle:"italic" }}>
                {rule.reviewerComment || (overrideReason + (comment ? ". " + comment : ""))}
              </div>
              <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:4, fontStyle:"italic" }}>
                Overridden by {hideReviewer ? "a reviewer" : (rule.reviewedBy || reviewerName)} · {new Date().toLocaleString()}
              </div>
            </div>
          )}

          {/* Saved confirm note display */}
          {saved && !isOverride && (rule.reviewerComment || comment) && (
            <div style={{ marginTop:8, padding:"8px 10px", background:"var(--gray-50, #f9fafb)", borderRadius:6, fontSize:12, color:"var(--text-secondary)", fontStyle:"italic" }}>
              Note: "{rule.reviewerComment || comment}" — {hideReviewer ? "Reviewer" : (rule.reviewedBy || reviewerName)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CRITERIA ACCORDION
   ═══════════════════════════════════════════════════════════════════ */
function CriteriaSection({ name, rules, expanded, onToggle, docId, reviewerName, isDocReviewed, onDecisionSaved, onFlagSaved, onViewWaveform, showNA }) {
    const pass = rules.filter(r => r.effectiveStatus === "PASSED").length;
    const fail = rules.filter(r => r.effectiveStatus === "FAILED").length;
    const na   = rules.filter(r => r.status === "NOT_APPLICABLE").length;
    const total = rules.length - na;
    const allPass = fail === 0 && total > 0;
    const visibleRules = showNA ? rules : rules.filter(r => r.status !== "NOT_APPLICABLE");

    return (
      <div style={{ borderBottom:"1px solid var(--border)" }}>
        <div onClick={onToggle} style={{
          padding:"10px 18px", display:"flex", alignItems:"center", gap:10, cursor:"pointer",
          background: expanded ? "var(--white)" : "var(--gray-50, #f9fafb)", transition:"background 0.1s" }}>
          <ChevronIcon open={expanded}/>
          <span style={{ fontSize:12.5, fontWeight:600, color:"var(--text-primary)", flex:1 }}>{name}</span>
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            {fail > 0 && <span style={{ fontSize:10, fontFamily:"var(--font-mono)", fontWeight:600, color:"#dc2626", background:"#fef2f2", border:"1px solid #fecaca", borderRadius:4, padding:"1px 7px" }}>{fail} fail</span>}
            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", fontWeight:500, color: allPass ? "#16a34a" : "var(--text-tertiary)",
              background: allPass ? "#f0fdf4" : "var(--gray-50, #f9fafb)", border:`1px solid ${allPass ? "#bbf7d0" : "var(--border)"}`, borderRadius:4, padding:"1px 7px" }}>
              {pass}/{total}
            </span>
          </div>
        </div>
        {expanded && (
          <div>
            {visibleRules.map(r => (
              <QARow key={r.ruleId} rule={r} docId={docId} reviewerName={reviewerName}
                isDocReviewed={isDocReviewed} onDecisionSaved={onDecisionSaved} onFlagSaved={onFlagSaved} onViewWaveform={onViewWaveform}/>
            ))}
          </div>
        )}
      </div>
    );
  }

/* ── One track's weighted score ────────────────────────────────────
   Points-weighted from the per-check max_points the backend sends on
   every rule. Callers pass rules for a single audit track; Technologist
   and Interpreting Physician are never pooled. effectiveStatus already
   reflects any saved reviewer override. Null when nothing is scorable,
   so an empty track shows as absent rather than as a zero.          */
// Below this share of applicable checks actually evaluated, a score stops
// being comparable to one drawn from a full set and is not shown at all.
// A track where nine of twenty checks could be answered, and all nine passed,
// is not a 100% - it is a case nobody could grade.
const COVERAGE_FLOOR = 0.7;

function trackStats(rules) {
  // effectiveStatus, not status: a check that came back UNDETERMINED and was
  // then decided by a reviewer carries a human judgement and counts. This
  // matches count_file() on the backend, which also evaluates after applying
  // overrides. Filtering N/A here rather than trusting the caller keeps every
  // call site on identical rules - the header and the section panels
  // previously disagreed because they filtered differently.
  const eff = r => r.effectiveStatus || r.status;
  const applicable = rules.filter(r => eff(r) !== "NOT_APPLICABLE");
  const evaluated = applicable.filter(r => eff(r) !== "UNDETERMINED");
  const weight = r => Number(r.points) > 0 ? Number(r.points) : 1;
  const possible = evaluated.reduce((t, r) => t + weight(r), 0);
  const earned = evaluated
    .filter(r => eff(r) === "PASSED")
    .reduce((t, r) => t + weight(r), 0);
  const raw = possible > 0 ? Math.round((earned / possible) * 100) : null;
  const coverage = applicable.length ? evaluated.length / applicable.length : 0;
  const thin = raw !== null && coverage < COVERAGE_FLOOR;
  return {
    score: thin ? null : raw,
    evaluated: evaluated.length,
    applicable: applicable.length,
    thin,
  };
}

function trackScoreOf(rules) {
  return trackStats(rules).score;
}

/* Renders "9 of 20 evaluated" under a score, and says so plainly when there
   were too few to score at all. */
function CoverageNote({ stats }) {
  if (!stats || stats.applicable === 0) return null;
  if (stats.evaluated === stats.applicable) return null;
  return (
    <span style={{ fontSize: 11, fontFamily: "var(--font-mono)",
      color: stats.thin ? "#b45309" : "var(--text-tertiary)" }}>
      {stats.evaluated} of {stats.applicable} evaluated
      {stats.thin && " — too few to score"}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   AUDIT TYPE SECTION — full expanded view
   ═══════════════════════════════════════════════════════════════════ */
function AuditTypeSection({ label, rules, docId, reviewerName, isDocReviewed, onDecisionSaved, onFlagSaved, onViewWaveform, isSideReviewed, sideReviewedBy, canSubmitSide, onSubmitSide }) {
  const applicable = rules.filter(r => r.status !== "NOT_APPLICABLE");
  const pass = applicable.filter(r => r.effectiveStatus === "PASSED").length;
  const fail = applicable.filter(r => r.effectiveStatus === "FAILED").length;
  const total = applicable.length;
  // pass/fail/total are check counts. The score is points-weighted, using the
  // per-check max_points the backend now sends on every rule.
  const stats = trackStats(applicable);
  const score = stats.score;
  const naCount = rules.length - total;

  const criteriaGroups = useMemo(() => {
    const groups = [];
    const seen = new Set();
    for (const r of rules) {
      const key = r.criteria || r.category || "General";
      if (!seen.has(key)) {
        seen.add(key);
        groups.push({ name: key, rules: [] });
      }
      groups.find(g => g.name === key).rules.push(r);
    }
    return groups;
  }, [rules]);

  const [expandedMap, setExpandedMap] = useState(() => {
    const m = {};
    criteriaGroups.forEach(g => {
      m[g.name] = g.rules.some(r => r.effectiveStatus === "FAILED");
    });
    return m;
  });
  const [viewFilter, setViewFilter] = useState("action"); // "action" | "passed" | "na" | "all"

  const toggleCriteria = name => setExpandedMap(p => ({ ...p, [name]: !p[name] }));
  const expandAll = () => setExpandedMap(criteriaGroups.reduce((m, g) => ({ ...m, [g.name]: true }), {}));
  const collapseAll = () => setExpandedMap(criteriaGroups.reduce((m, g) => ({ ...m, [g.name]: false }), {}));

  const headerColor = isSideReviewed ? "#6366f1" : fail > 0 ? "#dc2626" : "#16a34a";

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ padding:"14px 18px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", gap:14,
        borderLeft:`4px solid ${headerColor}` }}>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--text-primary)", letterSpacing:"-0.01em", textTransform:"uppercase" }}>
              {label}
            </div>
            {isSideReviewed && (
              <span style={{ fontSize:10, fontFamily:"var(--font-mono)", fontWeight:600, color:"#4f46e5", background:"#eef2ff",
                padding:"2px 8px", borderRadius:4, border:"1px solid #c7d2fe" }}>
                ✓ Reviewed{sideReviewedBy ? ` by ${sideReviewedBy}` : ""}
              </span>
            )}
          </div>
          <div style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"var(--text-secondary)", marginTop:2 }}>
            {pass} of {total} passed
            {fail > 0 && <span style={{ color:"#dc2626", fontWeight:600 }}> · {fail} failed</span>}
            {naCount > 0 && <span style={{ color:"var(--text-tertiary)" }}> · {naCount} n/a</span>}
            {stats.evaluated !== stats.applicable && <span> · <CoverageNote stats={stats}/></span>}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <ScoreRing score={score} size={48}/>
          {canSubmitSide && (
            <button onClick={onSubmitSide} className="btn btn-primary btn-sm" style={{ fontSize:11, fontFamily:"var(--font-mono)" }}>
              ✓ Submit
            </button>
          )}
          {fail > 0 && !isSideReviewed && (
            <button onClick={() => {
              const unaddressed = rules.filter(r => r.status === "FAILED" && !r.reviewerDecision);
              if (unaddressed.length === 0) return;
              if (!window.confirm(`Confirm all ${unaddressed.length} remaining failed checks as CONFIRMED_FAIL?`)) return;
              Promise.all(unaddressed.map(r =>
                submitReviewerDecision(docId, r.ruleId, "CONFIRMED_FAIL", "", reviewerName)
              )).then(() => {
                unaddressed.forEach(r => onDecisionSaved(r.ruleId, "CONFIRMED_FAIL"));
              }).catch(e => alert("Bulk confirm failed: " + (e?.message || String(e))));
            }} style={{ fontSize:10, fontFamily:"var(--font-mono)", padding:"4px 10px", borderRadius:5, cursor:"pointer",
              background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", fontWeight:500 }}>
              Confirm All ({fail - rules.filter(r => r.status === "FAILED" && r.reviewerDecision).length})
            </button>
          )}
          <div style={{ display:"flex", gap:4 }}>
            <button onClick={expandAll} style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", background:"none", border:"1px solid var(--border)", borderRadius:4, padding:"3px 7px", cursor:"pointer" }}>Expand all</button>
            <button onClick={collapseAll} style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", background:"none", border:"1px solid var(--border)", borderRadius:4, padding:"3px 7px", cursor:"pointer" }}>Collapse</button>
          </div>
        </div>
      </div>

      {/* Filter pills */}
        <div style={{ padding:"8px 18px", borderBottom:"1px solid var(--border)", display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
          {[
            { key:"action", label:`Needs Action (${fail + applicable.filter(r => r.effectiveStatus === "UNDETERMINED").length})`, show: fail > 0 || applicable.some(r => r.effectiveStatus === "UNDETERMINED") },
            { key:"passed", label:`Passed (${pass})`, show: pass > 0 },
            { key:"na", label:`N/A (${naCount})`, show: naCount > 0 },
            { key:"all", label:`All (${rules.length})`, show: true },
          ].filter(p => p.show).map(p => (
            <button key={p.key} onClick={() => setViewFilter(p.key)} style={{
              fontSize:10.5, fontFamily:"var(--font-mono)", fontWeight: viewFilter === p.key ? 600 : 400,
              padding:"3px 10px", borderRadius:4, cursor:"pointer", transition:"all 0.15s",
              background: viewFilter === p.key ? (p.key === "action" ? "#fef2f2" : p.key === "passed" ? "#f0fdf4" : p.key === "na" ? "#f8fafc" : "var(--blue-50)") : "none",
              color: viewFilter === p.key ? (p.key === "action" ? "#dc2626" : p.key === "passed" ? "#16a34a" : p.key === "na" ? "#64748b" : "var(--blue-700)") : "var(--text-tertiary)",
              border: `1px solid ${viewFilter === p.key ? (p.key === "action" ? "#fecaca" : p.key === "passed" ? "#bbf7d0" : p.key === "na" ? "#e2e8f0" : "var(--blue-200)") : "var(--border)"}`,
            }}>{p.label}</button>
          ))}
        </div>

        {/* Criteria groups — filtered by selected view */}
        {viewFilter !== "na" && criteriaGroups.filter(g => {
          const filtered = g.rules.filter(r => {
            if (viewFilter === "action") return r.effectiveStatus === "FAILED" || r.effectiveStatus === "UNDETERMINED";
            if (viewFilter === "passed") return r.effectiveStatus === "PASSED";
            return r.status !== "NOT_APPLICABLE"; // "all" shows everything except N/A inline
          });
          return filtered.length > 0;
        }).map(g => {
          const filteredRules = viewFilter === "all" ? g.rules : g.rules.filter(r => {
            if (viewFilter === "action") return r.effectiveStatus === "FAILED" || r.effectiveStatus === "UNDETERMINED";
            if (viewFilter === "passed") return r.effectiveStatus === "PASSED";
            return r.status !== "NOT_APPLICABLE";
          });
          return (
            <CriteriaSection key={g.name} name={g.name} rules={filteredRules}
              expanded={viewFilter === "action" ? true : !!expandedMap[g.name]}
              onToggle={() => toggleCriteria(g.name)}
              docId={docId} reviewerName={reviewerName} isDocReviewed={isDocReviewed}
              onDecisionSaved={onDecisionSaved} onFlagSaved={onFlagSaved} onViewWaveform={onViewWaveform}
              showNA={viewFilter === "all"}/>
          );
        })}

        {/* N/A view — grouped by reason */}
        {viewFilter === "na" && naCount > 0 && (() => {
          const naRules = rules.filter(r => r.status === "NOT_APPLICABLE");
          const reasonGroups = {};
          naRules.forEach(r => {
            const reason = r.aiEvidence || "Check not applicable to this case";
            const key = reason.length > 80 ? reason.substring(0, 80) : reason;
            if (!reasonGroups[key]) reasonGroups[key] = { reason, checks: [] };
            reasonGroups[key].checks.push(r);
          });
          return (
            <div style={{ padding:"12px 18px" }}>
              <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", marginBottom:10, textTransform:"uppercase", letterSpacing:"0.05em" }}>
                {naCount} checks excluded
              </div>
              {Object.values(reasonGroups).map((group, i) => (
                <details key={i} style={{ marginBottom:8, borderRadius:6, border:"1px solid var(--border)", overflow:"hidden" }}>
                  <summary style={{ padding:"8px 14px", fontSize:12, cursor:"pointer", background:"var(--gray-50, #f9fafb)",
                    display:"flex", justifyContent:"space-between", alignItems:"center", userSelect:"none", listStyle:"none" }}>
                    <span style={{ color:"var(--text-secondary)", flex:1 }}>{group.reason}</span>
                    <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", background:"var(--gray-100, #f3f4f6)",
                      padding:"1px 8px", borderRadius:4, marginLeft:8, flexShrink:0 }}>{group.checks.length}</span>
                  </summary>
                  <div style={{ padding:"6px 14px 10px", borderTop:"1px solid var(--border)" }}>
                    {group.checks.map(r => (
                      <div key={r.ruleId} style={{ padding:"4px 0", fontSize:12, color:"var(--text-tertiary)", display:"flex", gap:8 }}>
                        <span style={{ fontFamily:"var(--font-mono)", color:"#94a3b8", minWidth:36, flexShrink:0 }}>{r.ruleId}</span>
                        <span>{r.description}</span>
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          );
        })()}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   COLLAPSED AUDIT SUMMARY — compact one-line bar for non-selected type
   Click to expand into full AuditTypeSection
   ═══════════════════════════════════════════════════════════════════ */
function CollapsedAuditSummary({ label, rules, onExpand }) {
  const applicable = rules.filter(r => r.status !== "NOT_APPLICABLE");
  const pass = applicable.filter(r => r.effectiveStatus === "PASSED").length;
  const fail = applicable.filter(r => r.effectiveStatus === "FAILED").length;
  const total = applicable.length;
  const stats = trackStats(applicable);
  const score = stats.score;
  const scoreColor = score == null ? "var(--text-tertiary)"
                   : score >= 70 ? "#16a34a" : score >= 50 ? "#d97706" : "#dc2626";
  const isTech = label.includes("Tech");

  return (
    <div onClick={onExpand} className="card" style={{ marginBottom:16, cursor:"pointer", transition:"box-shadow 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.08)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}>
      <div style={{ padding:"12px 18px", display:"flex", alignItems:"center", gap:14,
        borderLeft:`4px solid ${isTech ? "#0891b2" : "#7c3aed"}`, background: isTech ? "#f8fdfe" : "#faf8ff" }}>
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={isTech ? "#0891b2" : "#7c3aed"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink:0 }}>
          <path d="M6 4l4 4-4 4"/>
        </svg>
        <div style={{ flex:1 }}>
          <span style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)", textTransform:"uppercase", letterSpacing:"-0.01em" }}>
            {label}
          </span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:12, flexShrink:0 }}>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:12, fontWeight:700, color: scoreColor }}>{score}%</span>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)" }}>{pass}/{total}</span>
          {fail > 0 && (
            <span style={{ fontSize:10, fontFamily:"var(--font-mono)", fontWeight:600, color:"#dc2626", background:"#fef2f2",
              border:"1px solid #fecaca", borderRadius:4, padding:"1px 7px" }}>
              {fail} fail
            </span>
          )}
          <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>click to expand</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   ALERTS SECTION — clinical events detected in document
   Expanded by default, collapsible
   ═══════════════════════════════════════════════════════════════════ */

const SEVERITY_STYLE = {
  HIGH:   { color:"#dc2626", bg:"#fef2f2", border:"#fecaca", dot:"#dc2626" },
  MEDIUM: { color:"#d97706", bg:"#fffbeb", border:"#fde68a", dot:"#d97706" },
  LOW:    { color:"#6b7280", bg:"#f9fafb", border:"#e5e7eb", dot:"#9ca3af" },
};

const ALERT_TYPE_LABELS = {
  SIGNAL_CHANGE:            "Signal Change",
  SURGEON_NOTIFIED:         "Surgeon Notified",
  ANESTHESIA_INTERVENTION:  "Anesthesia Intervention",
  POSITIONING_CHANGE:       "Positioning Change",
  MECHANICAL_CORRELATION:   "Mechanical Correlation",
  IP_CLINICAL_NOTE:         "IP Clinical Note",
  OTHER:                    "Other",
};

function AlertsSection({ alerts }) {
  // Starts collapsed: the header already says how many alerts there are and how
  // severe, which is the part a reviewer needs on arrival. The detail is a click
  // away rather than pushing the QA checks down the page.
  const [collapsed, setCollapsed] = useState(true);

  if (!alerts || alerts.length === 0) return null;

  const highCount   = alerts.filter(a => a.severity === "HIGH").length;
  const mediumCount = alerts.filter(a => a.severity === "MEDIUM").length;

  return (
    <div className="card" style={{ marginBottom:16, border:"1px solid #fecaca", overflow:"hidden" }}>
      {/* Header — always visible */}
      <div onClick={() => setCollapsed(c => !c)} style={{
        padding:"12px 18px", display:"flex", alignItems:"center", gap:12, cursor:"pointer",
        borderLeft:"4px solid #dc2626", background:"#fef2f2",
        transition:"background 0.1s" }}
        onMouseEnter={e => e.currentTarget.style.background = "#fde8e8"}
        onMouseLeave={e => e.currentTarget.style.background = "#fef2f2"}>
        <div style={{ width:28, height:28, borderRadius:6, background:"#fee2e2", border:"1px solid #fecaca",
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/>
          </svg>
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:13, fontWeight:700, color:"#991b1b", letterSpacing:"-0.01em" }}>
            {alerts.length} Clinical Alert{alerts.length !== 1 ? "s" : ""} Detected
          </div>
          <div style={{ fontSize:11, fontFamily:"var(--font-mono)", color:"#b91c1c", marginTop:1 }}>
            {highCount > 0 && <span>{highCount} high</span>}
            {highCount > 0 && mediumCount > 0 && <span> · </span>}
            {mediumCount > 0 && <span>{mediumCount} medium</span>}
            {highCount === 0 && mediumCount === 0 && <span>{alerts.length} event{alerts.length !== 1 ? "s" : ""}</span>}
          </div>
        </div>
        <ChevronIcon open={!collapsed}/>
      </div>

      {/* Alert rows */}
      {!collapsed && (
        <div>
          {alerts.map((alert, i) => (
            <AlertRow key={i} alert={alert} isLast={i === alerts.length - 1}/>
          ))}
        </div>
      )}
    </div>
  );
}

function AlertRow({ alert, isLast }) {
  const [showEvidence, setShowEvidence] = useState(true);
  const sev = SEVERITY_STYLE[alert.severity] || SEVERITY_STYLE.LOW;
  const typeLabel = ALERT_TYPE_LABELS[alert.type] || alert.type || "Event";

  return (
    <div style={{ borderBottom: isLast ? "none" : "1px solid var(--border)", padding:"10px 18px 10px 22px" }}>
      <div style={{ display:"flex", alignItems:"flex-start", gap:10 }}>
        {/* Severity dot */}
        <div style={{ marginTop:4, width:8, height:8, borderRadius:"50%", background:sev.dot, flexShrink:0 }}/>

        <div style={{ flex:1, minWidth:0 }}>
          {/* Top line: type + modality + time */}
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:3 }}>
            <span style={{ fontSize:12, fontWeight:600, color:"var(--text-primary)" }}>{typeLabel}</span>
            {alert.modality && (
              <span style={{ fontSize:10, fontFamily:"var(--font-mono)", fontWeight:500, padding:"1px 6px", borderRadius:3,
                background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }}>
                {alert.modality}
              </span>
            )}
            {alert.time && (
              <span style={{ fontSize:10, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)" }}>
                {alert.time}
              </span>
            )}
            <span style={{ fontSize:9.5, fontFamily:"var(--font-mono)", fontWeight:600, padding:"1px 6px", borderRadius:3,
              background:sev.bg, color:sev.color, border:`1px solid ${sev.border}`, textTransform:"uppercase" }}>
              {alert.severity || "—"}
            </span>
          </div>

          {/* Description */}
          <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>
            {alert.description}
          </div>

          {/* Evidence — collapsible */}
          {alert.evidence && (
            <>
              <button onClick={() => setShowEvidence(s => !s)} style={{
                marginTop:4, background:"none", border:"none", padding:0, cursor:"pointer",
                fontSize:10.5, fontFamily:"var(--font-mono)", color:"var(--text-tertiary)", display:"flex", alignItems:"center", gap:3 }}>
                <svg width="8" height="8" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  style={{ transition:"transform 0.15s", transform: showEvidence ? "rotate(90deg)" : "none" }}>
                  <path d="M6 4l4 4-4 4"/>
                </svg>
                {showEvidence ? "Hide" : "Show"} evidence
              </button>
              {showEvidence && (
                <div style={{ marginTop:4, padding:"6px 10px", background:"var(--gray-50, #f9fafb)", borderRadius:5,
                  border:"1px solid var(--border)", fontSize:11.5, fontFamily:"var(--font-mono)", color:"var(--text-secondary)",
                  lineHeight:1.5, whiteSpace:"pre-wrap" }}>
                  {alert.evidence}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   WAVEFORM TAB — unchanged from original
   ═══════════════════════════════════════════════════════════════════ */

function WaveformPlaceholder({ modality, pageNum, alert }) {
  const colors = { SSEP:"#2563eb", MEP: alert ? "#dc2626" : "#059669", EMG:"#0d9488", EEG:"#7c3aed", BAEP:"#d97706" };
  const c = colors[modality] || "#6b7280";
  return (
    <svg width="100%" height="100%" viewBox="0 0 300 160" style={{ display:"block" }}>
      <rect width="300" height="160" fill="#0a1f3d"/>
      {[40,80,120].map(y => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#1a3f7a" strokeWidth="0.5"/>)}
      {[60,120,180,240].map(x => <line key={x} x1={x} y1="0" x2={x} y2="160" stroke="#1a3f7a" strokeWidth="0.5" strokeDasharray="4 4"/>)}
      {[20,38,56,74,92,110,128].map((baseY, i) => (
        <g key={i}>
          <polyline points={`0,${baseY} 20,${baseY} 22,${baseY-8+i} 24,${baseY+12-i} 26,${baseY} 50,${baseY} 52,${baseY-10} 54,${baseY+14} 56,${baseY} 90,${baseY} 92,${baseY-9+i} 94,${baseY+11-i} 96,${baseY} 130,${baseY} 132,${baseY-8} 134,${baseY+10} 136,${baseY} 180,${baseY} 182,${baseY-11+i} 184,${baseY+13-i} 186,${baseY} 220,${baseY} 222,${baseY-9} 224,${baseY+12} 226,${baseY} 300,${baseY}`}
            fill="none" stroke={c} strokeWidth="1.2" opacity={0.85 - i*0.05}/>
        </g>
      ))}
      <text x="6" y="152" fontSize="8" fill={c} fontFamily="monospace" opacity="0.8">{modality} · pg {pageNum}</text>
    </svg>
  );
}

function WaveformLightbox({ group, pageIndex, onClose, onPrev, onNext }) {
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); if (e.key === "ArrowLeft") onPrev(); if (e.key === "ArrowRight") onNext(); };
    window.addEventListener("keydown", handler); return () => window.removeEventListener("keydown", handler);
  }, [onClose, onPrev, onNext]);
  const url = group.imageUrls?.[pageIndex];
  return (
    <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.88)", zIndex:2000, display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background:"var(--white)", borderRadius:12, overflow:"hidden", width:"80vw", maxWidth:960, boxShadow:"0 20px 60px rgba(0,0,0,0.5)", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"12px 18px", borderBottom:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"space-between", background:"var(--gray-50, #f9fafb)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:13, fontWeight:600 }}>{group.title}</span>
            <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-tertiary)" }}>Page {group.pages[pageIndex]} · {pageIndex+1}/{group.pages.length}</span>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <button onClick={onPrev} disabled={pageIndex === 0} className="btn btn-secondary btn-sm">← Prev</button>
            <button onClick={onNext} disabled={pageIndex === group.pages.length - 1} className="btn btn-secondary btn-sm">Next →</button>
            <button onClick={onClose} className="btn btn-ghost btn-sm">✕</button>
          </div>
        </div>
        <div style={{ background:"#0a1f3d", minHeight:400, display:"flex", alignItems:"center", justifyContent:"center" }}>
          {url ? <img src={url} alt={`${group.title} page ${group.pages[pageIndex]}`} style={{ maxWidth:"100%", maxHeight:"70vh", objectFit:"contain" }}/>
               : <div style={{ width:"100%", height:480 }}><WaveformPlaceholder modality={group.modality} pageNum={group.pages[pageIndex]} alert={group.alert}/></div>}
        </div>
      </div>
    </div>
  );
}

function WaveformTab({ data, decisions, onDecision, initialGroup }) {
  const defaultId = data.groups?.find(g => g.alert)?.id || data.groups?.[0]?.id || null;
  const [selectedId, setSelectedId] = useState(initialGroup || defaultId);
  const [lightbox, setLightbox] = useState(null);
  useEffect(() => { if (initialGroup) setSelectedId(initialGroup); }, [initialGroup]);
  const group = data.groups?.find(g => g.id === selectedId);
  const alertCount = data.groups?.filter(g => g.alert).length || 0;
  const baselineVoltage = group?.labels?.find(l => l.voltage != null)?.voltage || null;
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      <div className="card">
        <div className="card-header" style={{ borderLeft:"3px solid #14b8a6" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:6, background:"#f0fdfa", border:"1px solid #ccfbf1", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2"><polyline points="2 12 6 6 10 18 14 8 18 14 22 12"/></svg>
            </div>
            <div><div className="card-title">Waveform Analysis</div><div className="card-subtitle">GPT-4o Vision · Advisory only</div></div>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <span style={{ padding:"2px 8px", borderRadius:3, fontFamily:"var(--font-mono)", fontSize:11, fontWeight:500, background:"#f0fdfa", color:"#0f766e", border:"1px solid #ccfbf1" }}>{data.groups?.length || 0} forms</span>
            {alertCount > 0 && <span className="badge failed"><span className="badge-dot"/>{alertCount} Alert{alertCount>1?"s":""}</span>}
          </div>
        </div>
        <div style={{ padding:"10px 18px", borderBottom:"1px solid var(--border)", display:"flex", gap:6, flexWrap:"wrap", background:"var(--gray-50, #f9fafb)" }}>
          {data.groups?.map(g => (
            <button key={g.id} onClick={() => setSelectedId(g.id)} style={{
              padding:"4px 12px", borderRadius:20, fontSize:12, fontFamily:"var(--font-mono)", cursor:"pointer",
              border:`1px solid ${selectedId === g.id ? (g.alert ? "#dc2626" : "#14b8a6") : "var(--border)"}`,
              background: selectedId === g.id ? (g.alert ? "#fef2f2" : "#f0fdfa") : "var(--white)",
              color: selectedId === g.id ? (g.alert ? "#dc2626" : "#0f766e") : "var(--text-secondary)",
              fontWeight: selectedId === g.id ? 600 : 400 }}>
              {g.alert && "⚠ "}{g.title}
            </button>
          ))}
        </div>
        {group && (
          <div style={{ padding:18 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
              <span style={{ fontSize:16, fontWeight:600 }}>{group.title}</span>
              <span style={{ padding:"2px 8px", borderRadius:3, fontFamily:"var(--font-mono)", fontSize:11, background:"#eff6ff", color:"#1d4ed8", border:"1px solid #bfdbfe" }}>{group.modality}</span>
              {group.alert && <span className="badge failed"><span className="badge-dot"/>⚠ Voltage &gt;10%</span>}
              <span style={{ marginLeft:"auto", fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-tertiary)" }}>
                {group.pages.length > 1 ? `Pages ${group.pages[0]}–${group.pages[group.pages.length-1]}` : `Page ${group.pages[0]}`}
              </span>
            </div>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Pages — click to expand</div>
              <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                {group.pages.map((p, i) => (
                  <div key={i} onClick={() => setLightbox({ pageIndex:i })} style={{ width:140, cursor:"pointer", borderRadius:8, overflow:"hidden", border:"2px solid var(--border)", flexShrink:0 }}
                    onMouseEnter={e => e.currentTarget.style.borderColor="#14b8a6"} onMouseLeave={e => e.currentTarget.style.borderColor="var(--border)"}>
                    <div style={{ height:80, background:"#0a1f3d", pointerEvents:"none" }}>
                      {group.imageUrls?.[i] ? <img src={group.imageUrls[i]} alt={`pg ${p}`} style={{ width:"100%", height:"100%", objectFit:"cover" }}/> : <WaveformPlaceholder modality={group.modality} pageNum={p} alert={group.alert}/>}
                    </div>
                    <div style={{ padding:"5px 8px", background:"var(--gray-50, #f9fafb)", borderTop:"1px solid var(--border)", fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", display:"flex", justifyContent:"space-between" }}><span>Page {p}</span><span style={{ color:"#3b82f6" }}>⤢</span></div>
                  </div>
                ))}
              </div>
            </div>
            {group.labels?.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:8 }}>Voltage Readings</div>
                <div style={{ background:"var(--gray-50, #f9fafb)", border:"1px solid var(--border)", borderRadius:6, overflow:"hidden", maxHeight:220, overflowY:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                    <thead><tr style={{ borderBottom:"1px solid var(--border)" }}>
                      {["Time","Stimulation","Voltage","Change"].map(h => <th key={h} style={{ padding:"6px 12px", textAlign:"left", fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", fontWeight:500, position:"sticky", top:0, background:"var(--gray-50, #f9fafb)" }}>{h}</th>)}
                    </tr></thead>
                    <tbody>{group.labels.map((row, i) => {
                      const change = baselineVoltage && row.voltage != null && i > 0 ? (((row.voltage - baselineVoltage) / baselineVoltage) * 100).toFixed(1) : null;
                      const isAlert = change != null && Math.abs(parseFloat(change)) >= 10;
                      return (<tr key={i} style={{ borderBottom:"1px solid var(--border)", background: isAlert ? "#fff8f8" : "var(--white)" }}>
                        <td style={{ padding:"6px 12px", fontFamily:"var(--font-mono)", fontSize:11 }}>{row.time}</td>
                        <td style={{ padding:"6px 12px", fontFamily:"var(--font-mono)", fontSize:11, color:"var(--text-secondary)" }}>{row.stim}</td>
                        <td style={{ padding:"6px 12px", fontFamily:"var(--font-mono)", fontSize:11, fontWeight: i===0?600:400 }}>{row.voltage != null ? `${row.voltage} V` : "—"}{i===0 && <span style={{ marginLeft:6, fontSize:9, color:"#16a34a", background:"#f0fdf4", padding:"1px 4px", borderRadius:3 }}>baseline</span>}</td>
                        <td style={{ padding:"6px 12px" }}>{i===0 ? "—" : change != null ? <span style={{ fontFamily:"var(--font-mono)", fontSize:11, fontWeight:600, padding:"1px 5px", borderRadius:3, color: isAlert?"#dc2626":"#16a34a", background: isAlert?"#fef2f2":"#f0fdf4" }}>{change>0?"+":""}{change}%{isAlert?" ⚠":""}</span> : "—"}</td>
                      </tr>);
                    })}</tbody>
                  </table>
                </div>
              </div>
            )}
            <div style={{ padding:"8px 12px", background:"var(--gray-50, #f9fafb)", borderRadius:6, border:"1px solid var(--border)", marginBottom:12 }}>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", marginBottom:4 }}>GPT-4o Finding</div>
              <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.6 }}>{group.finding}</div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:16 }}>
              <div>
                <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, color:"var(--text-tertiary)", textTransform:"uppercase", marginBottom:4 }}>Confidence</div>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <div style={{ width:64, height:4, background:"var(--gray-200, #e5e7eb)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{ width:`${group.confidence}%`, height:"100%", borderRadius:2, background: group.confidence>=85?"#16a34a":group.confidence>=70?"#d97706":"#dc2626" }}/>
                  </div>
                  <span style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)" }}>{group.confidence}%</span>
                </div>
              </div>
              <div style={{ marginLeft:"auto", display:"flex", gap:6 }}>
                {["ACCEPT","FLAG"].map(v => {
                  const a = decisions[group.id] === v;
                  return <button key={v} onClick={() => onDecision(group.id, v)} style={{ padding:"4px 10px", borderRadius:6, fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer", fontWeight:500, border:`1px solid ${a?(v==="ACCEPT"?"#16a34a":"#dc2626"):"var(--border)"}`, background:a?(v==="ACCEPT"?"#f0fdf4":"#fef2f2"):"var(--white)", color:a?(v==="ACCEPT"?"#16a34a":"#dc2626"):"var(--text-secondary)" }}>{v==="ACCEPT"?"✓ Accept":"⚑ Flag"}</button>;
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      <div style={{ padding:"10px 14px", background:"#f0fdfa", border:"1px solid #ccfbf1", borderRadius:8, fontSize:12, color:"#0f766e", lineHeight:1.6 }}>
        <strong>Advisory only.</strong> Waveform analysis by GPT-4o Vision — all findings must be confirmed by a qualified reviewer.
      </div>
      {lightbox && group && <WaveformLightbox group={group} pageIndex={lightbox.pageIndex} onClose={() => setLightbox(null)}
        onPrev={() => setLightbox(l => ({ pageIndex:Math.max(0,l.pageIndex-1) }))}
        onNext={() => setLightbox(l => ({ pageIndex:Math.min(group.pages.length-1,l.pageIndex+1) }))}/>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════ */
/* One metric in the header score row: a ring, a label, and a sub-line.
   The open-item count rides in the label with only the digit in the alert
   color, so the red reads as a quantity rather than as punctuation. */
function Metric({ label, score, open = 0, sub }) {
  return (
    <div style={{ textAlign:"center", minWidth:52 }}>
      <div style={{ display:"flex", justifyContent:"center", marginBottom:5 }}>
        <ScoreRing score={score} size={46}/>
      </div>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:9.5, letterSpacing:"0.07em",
                    textTransform:"uppercase", color:"var(--text-tertiary)" }}>
        {label}
        {open > 0 && <> (<b style={{ color:"#A32D2D", fontWeight:700 }}>{open}</b>)</>}
      </div>
      {sub && (
        <div style={{ fontFamily:"var(--font-mono)", fontSize:10.5, color:"var(--text-tertiary)",
                      whiteSpace:"nowrap", fontVariantNumeric:"tabular-nums" }}>{sub}</div>
      )}
    </div>
  );
}

/* The header score row. Sections are the people and processes being scored;
   dividers of equal weight sit between them, and the two group headings span
   their sections above. Built from an array so a section can be absent (a tech
   viewer sees no IP, a case with no MSO run shows no MSO) without the grid
   maths going wrong, and so the next track is one more entry rather than a
   redesign. */
function AuditScoreRow({ sections }) {
  if (!sections.length) return null;
  const auditCount = sections.filter(s => s.group === "audit").length;
  const cols = sections.map(() => "auto").join(" 1px ");
  const labelStyle = {
    fontFamily:"var(--font-mono)", fontSize:10, letterSpacing:"0.11em",
    textTransform:"uppercase", fontWeight:600, color:"var(--text-tertiary)",
  };
  // Section i sits in column 2i+1; the divider before it in column 2i.
  const auditEnd = auditCount * 2;          // the divider that starts "Other"
  const hasOther = sections.length > auditCount;

  return (
    <div style={{ display:"grid", gridTemplateColumns:cols,
                  gridTemplateRows:"auto auto", gap:"9px 20px", alignItems:"stretch" }}>
      {auditCount > 0 && (
        <div style={{ ...labelStyle, gridRow:1,
             gridColumn: hasOther ? `1 / ${auditEnd}` : "1 / -1" }}>Audit</div>
      )}
      {hasOther && (
        <div style={{ ...labelStyle, gridRow:1, gridColumn:`${auditEnd} / -1`, textAlign:"left" }}>
          Other reviews
        </div>
      )}
      {sections.map((s, i) => (
        <React.Fragment key={s.key}>
          {i > 0 && <div style={{ gridRow:2, background:"var(--border)" }}/>}
          <div style={{ gridRow:2 }}>
            <div style={{ fontSize:11.5, fontWeight:600, color:s.tint, marginBottom:7 }}>{s.name}</div>
            <div style={{ display:"flex", gap:16 }}>
              {s.metrics.map(m => <Metric key={m.label} {...m}/>)}
            </div>
            {s.signoff && (
              <div style={{ marginTop:8, fontFamily:"var(--font-mono)", fontSize:10.5, color:"#1D9E75" }}>
                {s.signoff}
              </div>
            )}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
}

/* One row in the header's overflow menu. A disabled row still shows its hint,
   which is where the reason lives, so a greyed-out action always says why. */
function MenuItem({ label, hint, onClick, disabled = false }) {
  return (
    <button role="menuitem" onClick={disabled ? undefined : onClick} disabled={disabled}
      style={{ display:"block", width:"100%", textAlign:"left", padding:"8px 10px",
               background:"none", border:"none", borderRadius:6,
               cursor: disabled ? "default" : "pointer", fontSize:12.5,
               opacity: disabled ? 0.55 : 1,
               color:"var(--text-primary)" }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = "var(--gray-50, #f9fafb)"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "none"; }}>
      {label}
      {hint && (
        <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:2 }}>{hint}</div>
      )}
    </button>
  );
}

export default function DocumentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { accounts } = useMsal();
  const reviewerName = accounts?.[0]?.name || accounts?.[0]?.username || "Reviewer";
  const { isAdmin, isTech, isIP } = useRoles();

  // Read audit type from URL params (carried from Documents page) or default to "Both"
  const _urlAudit = searchParams.get("audit") || "Both";
  const initialAudit = isTech ? "Technologist"
                     : isIP ? "Interpreting_Physician"
                     : _urlAudit;

  const [doc, setDoc]                   = useState(null);
  const [qa, setQa]                     = useState(null);
  const initialTab = (isIP ? ["qa"] : ["qa", "waveform", "mso", "idr"])
    .includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "qa";

  const [tab, setTab]                   = useState(initialTab);
  const [pdfUrl, setPdfUrl]             = useState(null);
  const [showPdf, setShowPdf]           = useState(false);
  const [siblingPdfs, setSiblingPdfs]   = useState([]);
  const [activePdfIdx, setActivePdfIdx] = useState(0);
  const [screenshotFileName, setScreenshotFileName] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [decisions, setDecisions]       = useState({});
  const [completing, setCompleting]     = useState(false);
  const [summary, setSummary]           = useState("");
  const [showComplete, setShowComplete] = useState(null); // null | "Technologist" | "IP" | "legacy"
  const [showClose, setShowClose]       = useState(false);
  const [closeNote, setCloseNote]       = useState("");
  // The close call runs a server-side sign-off check and a Cosmos write, so it
  // takes several seconds. Without a busy state the modal sat there looking
  // dead and invited a second click.
  const [closing, setClosing]           = useState(false);
  // Overflow menu for actions that are real but rare. Keeping them out of the
  // header stops the row growing every time a case-level action is added.
  const [menuOpen, setMenuOpen]         = useState(false);
  const menuRef                         = useRef(null);
  const [showAbort, setShowAbort]       = useState(false);
  const [abortReason, setAbortReason]   = useState("");
  const [aborting, setAborting]         = useState(false);
  const [waveformData, setWaveformData] = useState(null);
  const [wfDecisions, setWfDecisions]   = useState({});
  const [wfSelectedGroup, setWfSelectedGroup] = useState(null);
  const [auditType, setAuditType]       = useState(initialAudit);
  const [availableSources, setAvailableSources] = useState(new Set());
  const [pidTracker, setPidTracker]     = useState(null);

  const handleViewWaveform = useCallback(gid => { setWfSelectedGroup(gid); setTab("waveform"); }, []);

  // Original data loading — unchanged, never breaks
  useEffect(() => {
    Promise.all([getDocument(id), getQAResult(id)])
      .then(([d, q]) => { setDoc(d); setQa(q); })
      .finally(() => setLoading(false));
    getPdfUrl(id).then(r => setPdfUrl(r?.url || null)).catch(() => {});
    getWaveformAnalysis(id).then(w => setWaveformData(w || null)).catch(() => {});
  }, [id]);

  // Separate effect: fetch sibling PDFs for PID_QA docs (tab bar + source tracking)
  useEffect(() => {
    if (!doc || doc.sourceType !== "PID_QA" || !doc.patientId) return;
    getDocuments({}).then(allDocs => {
      const siblings = allDocs.filter(
        s => s.patientId === doc.patientId && s.sourceType && s.sourceType !== "PID_QA" && s.sourceType !== "Combined"
      );
      // Build available sources set
      const sources = new Set(siblings.map(s => s.sourceType).filter(Boolean));
      setAvailableSources(sources);

      // Find the screenshot file for inline evidence rendering
      const screenshot = siblings.find(s => s.sourceType === "Usmon Screenshots");
      setScreenshotFileName(screenshot ? screenshot.filename : null);

      if (siblings.length > 0) {
        Promise.all(siblings.map(s =>
          getPdfUrl(s.id).then(r => ({ filename: s.filename, sourceType: s.sourceType, url: r?.url || null })).catch(() => ({ filename: s.filename, sourceType: s.sourceType, url: null }))
        )).then(results => {
          const withUrls = results.filter(r => r.url);
          if (withUrls.length > 0) {
            setSiblingPdfs(withUrls);
            setPdfUrl(withUrls[0].url);
          }
        });
      }
    }).catch(() => {});
  }, [doc]); // eslint-disable-line

  // Fetch the PID tracker for the "Last QA'd" indicator + Re-run polling
  useEffect(() => {
    if (!doc?.patientId) return;
    getPidTrackers().then(list => {
      const t = (list || []).find(x => (x.id || x.patient_id) === doc.patientId);
      if (t) setPidTracker(t);
    }).catch(() => {});
  }, [doc]);

  const handleDecisionSaved = useCallback((ruleId, decision) => {
    setDecisions(prev => ({ ...prev, [ruleId]: decision }));
  }, []);

  // Flags raised or answered since this page loaded. The rules themselves come
  // from the QA payload fetched on mount, so without this the banner would
  // keep counting a flag the reviewer just closed.
  const [flagOverrides, setFlagOverrides] = useState({});
  const handleFlagSaved = useCallback((ruleId, flag) => {
    setFlagOverrides(prev => ({ ...prev, [ruleId]: flag }));
  }, []);

  const handleCompleteReview = async () => {
    setCompleting(true);
    try {
      await completeReview(id, reviewerName, summary, showComplete || "");
      clearDocumentsCache();
      const q = await getQAResult(id);
      setQa(q); setShowComplete(null); setSummary("");
    } catch { alert("Failed to complete review."); }
    setCompleting(false);
  };

  // The destructive "reset the whole review" action used to live here. It is
  // gone on purpose: a re-run now clears the decisions it supersedes, so
  // Reopen followed by Re-run already produces a clean slate, and a one-click
  // button that deletes every sign-off and override next to Reopen was an
  // accident waiting to happen. The unlock-review endpoint still exists.

  // One close path, used by the header modal and by waveform sign-off, so both
  // land in the same state without a page reload.
  const doCloseCase = useCallback(async (note = "") => {
    await closeDocument(id, reviewerName, note);
    clearDocumentsCache();
    const q = await getQAResult(id);
    setQa(q);
    setDoc(d => d ? { ...d, isClosed:true, reviewerStatus:"CLOSED" } : d);
  }, [id, reviewerName]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = e => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey  = e => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  // Waveform and MSO are scored elsewhere and are not in the QA payload, so the
  // header fetches them itself. Both are best-effort: a header metric must
  // never be the reason the page fails to render.
  const [wfRec, setWfRec]   = useState(null);
  const [msoRec, setMsoRec] = useState(null);
  useEffect(() => {
    const caseId = doc?.patientId;
    if (!caseId) return undefined;
    let cancelled = false;
    getWaveformQa(caseId)
      .then(r => { if (!cancelled) setWfRec(r || null); })
      .catch(() => {});
    getMsoStatusBulk([String(caseId)])
      .then(m => { if (!cancelled) setMsoRec((m || {})[String(caseId)] || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [doc?.patientId]);

  // Abort marks a case that should never have been QA'd (cancelled or
  // abandoned procedure). It hides the case and takes it out of every score,
  // but deletes nothing, so it is undone with Restore.
  const doAbort = async (reason) => {
    setAborting(true);
    try {
      await abortDocument(id, reviewerName, reason);
      clearDocumentsCache();
      setDoc(d => d ? { ...d, isAborted:true, reviewerStatus:"ABORTED" } : d);
      setShowAbort(false); setAbortReason("");
    } catch (e) { alert("Failed: " + (e?.message || String(e))); }
    setAborting(false);
  };

  const doRestore = async () => {
    if (!window.confirm("Restore this case? It returns to the case list and starts counting again.")) return;
    setAborting(true);
    try {
      await restoreDocument(id, reviewerName);
      clearDocumentsCache();
      setDoc(d => d ? { ...d, isAborted:false, reviewerStatus:"" } : d);
    } catch (e) { alert("Failed: " + (e?.message || String(e))); }
    setAborting(false);
  };

  // Reopen is the non-destructive counterpart to Close: it lifts the lock and
  // lets QA run again, but keeps every sign-off and override in place.
  const [reopening, setReopening] = useState(false);
  const handleReopen = async () => {
    if (!window.confirm("Reopen this case? Sign-offs stay as they are - this only unlocks the case so QA can be re-run.")) return;
    setReopening(true);
    try {
      await reopenDocument(id, reviewerName);
      clearDocumentsCache();
      const q = await getQAResult(id);
      setQa(q);
      setDoc(d => d ? { ...d, isClosed:false } : d);
    } catch (e) { alert("Failed: " + (e?.message || String(e))); }
    setReopening(false);
  };

  // Lift one side's sign-off so that side can be changed and signed again.
  // Narrow on purpose: the overrides and notes stay, which is what separates
  // this from the whole-review reset that used to live next to Reopen.
  const [unlocking, setUnlocking] = useState("");
  const handleUnlockSide = async (side) => {
    const label = side === "Technologist" ? "Technologist" : "Interpreting Physician";
    if (!window.confirm(`Unlock the ${label} sign-off? Every override and note is kept - this only reopens that side so it can be changed and signed again.`)) return;
    setUnlocking(side);
    try {
      await unlockReview(id, reviewerName, side);
      clearDocumentsCache();
      const q = await getQAResult(id);
      setQa(q);
    } catch (e) { alert("Failed: " + (e?.message || String(e))); }
    setUnlocking("");
  };

  // The waveform record is signed from its own tab, but a reviewer reopening a
  // case works from the header, so the same unlock lives in both places. The
  // record is already loaded here, so this re-saves it with the sign-off lifted
  // rather than fetching it again; the score is recomputed server-side either way.
  const [unlockingWave, setUnlockingWave] = useState(false);
  const handleUnlockWaveform = async () => {
    if (!window.confirm("Reopen the waveform QA? The answers stay as they are - this only lifts the sign-off so they can be changed and signed again.")) return;
    setUnlockingWave(true);
    try {
      const caseId = doc?.patientId || id;
      const saved = await saveWaveformQa(caseId, {
        present_modalities: wfRec?.present_modalities || [],
        answers:  wfRec?.answers  || {},
        values:   wfRec?.values   || {},
        comments: wfRec?.comments || {},
        signed: false,
      });
      setWfRec(saved || { ...(wfRec || {}), signed: false });
      clearDocumentsCache();
    } catch (e) { alert("Failed: " + (e?.message || String(e))); }
    setUnlockingWave(false);
  };

  // The monthly waveform list is built by the backend; this is the reviewer's
  // override for a case it did or did not choose.
  const [savingWaveSel, setSavingWaveSel] = useState(false);
  const onWaveformList = !!doc?.waveformSelected;
  const handleToggleWaveformList = async () => {
    setSavingWaveSel(true);
    try {
      const caseId = doc?.patientId || id;
      if (onWaveformList) await removeFromWaveformSelection(caseId);
      else await addToWaveformSelection(caseId);
      setDoc(d => d ? { ...d, waveformSelected: !onWaveformList } : d);
      clearDocumentsCache();
    } catch (e) { alert("Failed: " + (e?.message || String(e))); }
    setSavingWaveSel(false);
  };

  const [rerunning, setRerunning] = useState(false);

  // QA is "running" straight from the tracker, so the indicator survives a refresh.
  const qaRunning = pidTracker?.status === "QA_RUNNING";

  // Poll while a re-run is in flight — covers a fresh re-run (optimistic set below)
  // and a mid-run page refresh (tracker fetched as QA_RUNNING on mount).
  useEffect(() => {
    if (!doc?.patientId || pidTracker?.status !== "QA_RUNNING") return;
    const pid = doc.patientId;
    const baseline = pidTracker?.last_qa_at || null;

    // QA_RUNNING is set optimistically when a re-run is triggered, so if the
    // run never actually starts this poll had nothing to end it and ran until
    // the tab was closed. At a 5s interval against a call that takes 4-7s the
    // requests also overlapped and stacked. Hence: a longer interval than the
    // call takes, a guard against overlapping ticks, and a hard stop.
    const POLL_MS = 10000;
    const GIVE_UP_MS = 20 * 60 * 1000;   // longest Combined case is ~15 min
    const startedAt = Date.now();
    let inFlight = false;

    const iv = setInterval(async () => {
      if (inFlight) return;              // a slow tick must not stack on itself
      if (Date.now() - startedAt > GIVE_UP_MS) {
        clearInterval(iv);
        // Resolve the optimistic status against whatever the server actually
        // says, so the page stops claiming a run is in progress.
        try {
          const list = await getPidTrackers();
          const t = (list || []).find(x => (x.id || x.patient_id) === pid);
          if (t) setPidTracker(t);
        } catch { /* leave it as-is */ }
        return;
      }
      inFlight = true;
      try {
        const list = await getPidTrackers();
        const t = (list || []).find(x => (x.id || x.patient_id) === pid);
        if (!t) return;

        // Still going: QUEUED counts, a queued run has not started yet.
        if (t.status === "QA_RUNNING" || t.status === "QUEUED") {
          setPidTracker(t);
          return;
        }

        // A terminal status. The backend writes last_qa_at a moment *after*
        // it sets QA_COMPLETE, so a poll can catch the status flipped with the
        // old timestamp still on it. Inside the settle window that is treated
        // as "not finished yet" and we keep polling WITHOUT touching state -
        // setting the tracker here would tear down this effect and nothing
        // would ever refetch, which is exactly the stale-scores bug.
        const SETTLE_MS = 60000;
        const isFreshResult = t.last_qa_at && t.last_qa_at !== baseline;
        const settled = Date.now() - startedAt > SETTLE_MS;
        if (!isFreshResult && !settled) return;

        // Finished. Refresh the page's data FIRST, then adopt the tracker, so
        // the banner and the scores change together rather than the banner
        // clearing over numbers from the previous run. Refetch on every
        // ending, not just success: a failed run changes what the page should
        // say too.
        clearInterval(iv);
        try {
          const [d, q] = await Promise.all([getDocument(id), getQAResult(id)]);
          setDoc(d); setQa(q);
        } finally {
          setPidTracker(t);
        }
      } catch { /* keep polling */ }
      finally { inFlight = false; }
    }, POLL_MS);
    return () => clearInterval(iv);
  }, [doc, pidTracker?.status, id]); // eslint-disable-line

  const handleRerunQA = async () => {
    if (!window.confirm("Re-run QA on this document? Existing results will be replaced. This takes 5–15 minutes for Combined cases and 2–3 minutes for PID cases.")) return;
    const pid = doc?.patientId;
    setRerunning(true);
    try {
      const res = await rerunDocumentQA(doc?.id || id, reviewerName);
      clearDocumentsCache();
      if (pid && res?.type !== "combined") {
        // Flip the tracker to QA_RUNNING immediately so the banner + Documents page
        // show "running" without waiting; the polling effect takes it from here.
        setPidTracker(p => ({ ...(p || {}), status: "QA_RUNNING" }));
      } else {
        alert(res?.message || "QA re-run triggered. Refresh in a few minutes to see updated results.");
      }
    } catch (e) {
      alert("Re-run failed: " + (e?.message || String(e)));
    }
    setRerunning(false);
  };

  // ── Derived data ────────────────────────────────────────────
  const isPidQa = doc?.sourceType === "PID_QA";
  const allRules    = useMemo(() => {
    const rules = qa?.categories?.flatMap(c => c.rules) || [];
    // Inject available sources + screenshot filename for badge & evidence rendering
    if (isPidQa) rules.forEach(r => {
      r._availableSources = availableSources;
      r._screenshotFileName = screenshotFileName;
    });
    // effectiveStatus = reviewer-override-adjusted status. Used everywhere we
    // COUNT (scores, pass/fail tallies, filter pills, "Needs Action" filter).
    // Raw r.status is preserved for icons + action-button visibility so a
    // FAIL→PASS override still renders the override badge over a FAIL row.
    rules.forEach(r => {
      const d = r.reviewerDecision || decisions[r.ruleId];
      r.effectiveStatus = d === "OVERRIDE_PASS" ? "PASSED"
                        : d === "OVERRIDE_FAIL" ? "FAILED"
                        : r.status;
    });
    return rules;
  }, [qa, isPidQa, availableSources, screenshotFileName, decisions]);
  const failedRules = useMemo(() => allRules.filter(r => r.effectiveStatus === "FAILED"), [allRules]);
  const failedCount = failedRules.length;
  // A check nobody could grade is not a pass. Scoring drops UNDETERMINED from
  // the denominator, so a case that graded nine of twenty checks and failed
  // none of them used to read "QA Passed" at the top of the card.
  const undeterminedCount = useMemo(
    () => allRules.filter(r => (r.effectiveStatus || r.status) === "UNDETERMINED").length,
    [allRules]);
  const isReviewed  = qa?.isReviewed || false;
  const isClosed    = qa?.isClosed || doc?.isClosed || false;
  const isAborted   = doc?.isAborted || doc?.reviewerStatus === "ABORTED" || false;
  const isDone      = isReviewed || isClosed;

  // Per-audit-type review state from backend
  const techReviewed = qa?.techReviewed || false;
  const ipReviewed   = qa?.ipReviewed || false;

  const sortedRules = useMemo(() => [...allRules].sort((a, b) => {
    const na = parseInt(a.ruleId, 10) || 0;
    const nb = parseInt(b.ruleId, 10) || 0;
    return na - nb;
  }), [allRules]);

  // Checks a technologist asked someone to look at. Advisory, so it surfaces
  // as a banner rather than as a status: nothing about the case has changed.
  const flaggedRules = useMemo(
    () => allRules
      .map(r => ({ rule: r, flag: (r.ruleId in flagOverrides) ? flagOverrides[r.ruleId] : r.techFlag }))
      .filter(x => x.flag && x.flag.open !== false),
    [allRules, flagOverrides]);

  const techRules = useMemo(() => sortedRules.filter(r => r.auditType === "Technologist"), [sortedRules]);
  const ipRules   = useMemo(() => sortedRules.filter(r => r.auditType === "Interpreting_Physician"), [sortedRules]);
  const hasAuditTypes = (techRules.length + ipRules.length) >= allRules.length * 0.5;

  const applicableRules = allRules.filter(r => r.status !== "NOT_APPLICABLE");
  // Counts for the caption underneath. Checks, not points - and scoped to the
  // Technologist track so they describe the same thing the ring above them
  // does. They previously summed Tech and IP while the ring showed Tech only,
  // so the caption and the score disagreed by construction.
  const techApplicable = techRules.filter(
    r => (r.effectiveStatus || r.status) !== "NOT_APPLICABLE");

  // Per-track stats from trackStats, the same function the section panels use,
  // so the header and the panels below it cannot report different numbers.
  // effectiveStatus already reflects unsaved reviewer decisions, so a score
  // still moves live as checks are overridden. There is deliberately no single
  // headline score: Technologist and Interpreting Physician are separate
  // audits of separate people and pooling them describes neither.
  const techStats = trackStats(techRules);
  const ipStats   = trackStats(ipRules);

  // Per-type failed rules and addressed counts
  const techFailed = useMemo(() => techRules.filter(r => r.effectiveStatus === "FAILED"), [techRules]);
  const ipFailed   = useMemo(() => ipRules.filter(r => r.effectiveStatus === "FAILED"), [ipRules]);
  const techAddressed = techFailed.filter(r => r.reviewerDecision || decisions[r.ruleId]).length;
  const ipAddressed   = ipFailed.filter(r => r.reviewerDecision || decisions[r.ruleId]).length;
  const techAllAddressed = techFailed.length > 0 && techAddressed === techFailed.length;
  const ipAllAddressed   = ipFailed.length > 0 && ipAddressed === ipFailed.length;

  // Per-type submit eligibility (has failures → all addressed)
  const canSubmitTech = techAllAddressed && !techReviewed && !isDone;
  const canSubmitIP   = ipAllAddressed && !ipReviewed && !isDone;

  // Per-type mark-reviewed eligibility (no failures → ready to sign off)
  const canMarkTech = techFailed.length === 0 && techRules.length > 0 && !techReviewed && !isDone;
  const canMarkIP   = ipFailed.length === 0 && ipRules.length > 0 && !ipReviewed && !isDone;

  // Global completion (legacy — for non-audit-type documents)
  const addressedCount = failedRules.filter(r => r.reviewerDecision || decisions[r.ruleId]).length;
  const allAddressed   = failedCount > 0 && addressedCount === failedCount;
  const canComplete    = !hasAuditTypes && allAddressed && !isDone;
  // Legacy sign-off shortcut, only for documents with no audit types. Cases
  // that DO have tracks are signed off per track (canMarkTech / canMarkIP), so
  // this button can no longer be used to skip a track's human stamp.
  const canMarkReviewed = !hasAuditTypes && failedCount === 0 && allRules.length > 0 && !isDone;
  // Closing is the last step: every track that has rules has been signed off,
  // and the reviewer is confirming no manual waveform work is coming.
  const canClose = !isTech && !isClosed && allRules.length > 0 &&
                   (techRules.length === 0 || techReviewed) &&
                   (ipRules.length === 0 || ipReviewed) &&
                   (techReviewed || ipReviewed);

  // The header's score sections. One entry per person or process being scored,
  // in reading order, with the group each belongs to.
  const headerSections = useMemo(() => {
    const out = [];
    const sub = (stats, passed) =>
      stats.applicable === 0 ? "no checks"
      : stats.thin        ? `${stats.evaluated}/${stats.applicable} graded`
      : `${passed}/${stats.applicable}`;

    if (techRules.length > 0) {
      const metrics = [{
        label: "Doc",
        score: techStats.score,
        open:  techFailed.length,
        sub:   sub(techStats, techApplicable.filter(r => (r.effectiveStatus || r.status) === "PASSED").length),
      }];
      // Waveform is the Technologist's second metric, not a track of its own.
      metrics.push({
        label: "Wave",
        score: typeof wfRec?.score === "number" ? wfRec.score : null,
        open:  0,
        sub:   wfRec?.signed ? "reviewed" : "not reviewed",
      });
      out.push({
        key: "tech", group: "audit", name: "Technologist", tint: "#0891b2",
        metrics,
        signoff: techReviewed ? `\u2713 Reviewed${!isTech && qa?.techReviewedBy ? ` by ${qa.techReviewedBy}` : ""}` : null,
      });
    }

    if (!isTech && ipRules.length > 0) {
      const ipApplicable = ipRules.filter(r => (r.effectiveStatus || r.status) !== "NOT_APPLICABLE");
      out.push({
        key: "ip", group: "audit", name: "Interp. Physician", tint: "#7c3aed",
        metrics: [{
          label: "Doc",
          score: ipStats.score,
          open:  ipFailed.length,
          sub:   sub(ipStats, ipApplicable.filter(r => (r.effectiveStatus || r.status) === "PASSED").length),
        }],
        signoff: ipReviewed ? `\u2713 Reviewed${qa?.ipReviewedBy ? ` by ${qa.ipReviewedBy}` : ""}` : null,
      });
    }

    // MSO is always shown, run or not, so the row is the full picture of what a
    // case is scored on rather than only what happens to exist today. A state
    // that is not a score names itself on the sub-line: "running" and "no
    // screenshots" are different problems and should not both read "not run".
    if (!isTech) {
      const st     = msoRec?.status || "none";
      const scored = typeof msoRec?.score === "number";
      const done   = st === "approved" || st === "rejected";
      const sub =
          st === "running"     ? "running"
        : st === "unavailable" ? "no screenshots"
        : st === "error"       ? "run failed"
        : !scored              ? "not run"
        : done                 ? "reviewed"
        : "not reviewed";
      out.push({
        key: "mso", group: "other", name: "MSO", tint: "#b45309",
        metrics: [{
          label: "Score",
          score: scored ? msoRec.score : null,
          open:  msoRec?.discrepancyCount || 0,
          sub,
        }],
        signoff: null,
      });
    }

    return out;
  }, [techRules, ipRules, techStats, ipStats, techFailed, ipFailed, techApplicable,
      techReviewed, ipReviewed, isTech, qa, wfRec, msoRec]);

  // "Last touched" is whatever happened to this case most recently, whichever
  // kind of event that was: an individual check override, either track's
  // sign-off, the close, a QA run, or failing all of those, the upload.
  // Waveform sign-off is not in this payload, so it does not count yet.
  const lastTouched = useMemo(() => {
    const stamps = [
      qa?.reviewedAt,
      qa?.techReviewedAt,
      qa?.ipReviewedAt,
      pidTracker?.last_qa_at,
      doc?.uploadedAt,
      ...allRules.map(r => r.reviewedAt),
    ]
      .filter(Boolean)
      .map(s => new Date(s))
      .filter(d => !isNaN(d.getTime()));
    if (stamps.length === 0) return null;
    return new Date(Math.max(...stamps.map(d => d.getTime())));
  }, [qa, pidTracker, doc, allRules]);

  // Why a re-run is not available right now, or empty when it is. Doubles as
  // the menu item's hint, so the reason is visible rather than hidden behind a
  // greyed-out row with no explanation.
  const rerunBlockedReason =
      isClosed  ? "Closed. Reopen the case first."
    : isAborted ? "Aborted. Restore the case first."
    : "";
  const showMenu = !isTech || isAdmin;

  if (loading) return <div className="page-body"><div className="skeleton" style={{height:200,borderRadius:10}}/></div>;
  if (!doc) return (
    <div className="page-body">
      <div style={{ padding:48, textAlign:"center", color:"var(--text-tertiary)" }}>
        <div style={{ fontSize:16, fontWeight:600, marginBottom:8 }}>Document not found</div>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate("/documents")}>← Back</button>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="page-header">
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate("/documents")}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{width:14,height:14}}><path d="M10 3L5 8l5 5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back
          </button>
          <div style={{ width:1, height:28, background:"var(--border)" }}/>
          <div className="page-title-group">
            {/* Closed reads off the title itself. The old header chip carried the
                same fact plus a reviewer name and wrapped onto three lines. */}
            <div className="page-title" style={{ fontSize:14, display:"inline-flex", alignItems:"center", gap:6,
                 color: isClosed ? "#16a34a" : undefined }}>
              {doc.filename}
              {isClosed && (
                <span title={`Closed${qa?.reviewedBy && !isTech ? ` by ${qa.reviewedBy}` : ""}`}
                      style={{ fontSize:12, lineHeight:1 }}>🔒</span>
              )}
            </div>
            {pidTracker?.last_qa_at && (
              <div className="page-subtitle" style={{ display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
                <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 8a6 6 0 0 1-10.5 4M2 8a6 6 0 0 1 10.5-4" strokeLinecap="round"/>
                  <polyline points="2 4 2 8 6 8" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14 12 14 8 10 8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Last QA'd {new Date(pidTracker.last_qa_at).toLocaleString()}{pidTracker.last_qa_by && pidTracker.last_qa_by !== "Unknown" ? ` by ${pidTracker.last_qa_by}` : ""}
              </div>
            )}
          </div>
        </div>
        <div className="header-actions">
          {isAborted ? (
            <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"#6b7280", background:"#f9fafb", padding:"4px 10px", borderRadius:6, border:"1px solid #e5e7eb" }}
                  title="This case is excluded from the case list and from every score. Nothing has been deleted. Restore it from the menu.">
              Aborted
            </span>
          ) : isClosed ? (
            !isTech ? (
              <button className="btn btn-secondary btn-sm" onClick={handleReopen} disabled={reopening}
                      title="Reopen this case. Sign-offs are kept - this only unlocks it so QA can be re-run."
                      style={{ fontSize:11, color:"#d97706" }}>
                🔓 {reopening ? "Reopening…" : "Reopen"}
              </button>
            ) : null
          ) : !canClose ? <StatusBadge status={doc.status}/> : null}
          {pdfUrl && <button className="btn btn-secondary btn-sm" onClick={() => setShowPdf(p => !p)}>{showPdf ? "Hide PDF" : "View PDF"}</button>}
          {!isTech && <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/agent?docId=${id}`)}>Ask AI →</button>}
          {!isTech && canComplete && <button className="btn btn-primary btn-sm" onClick={() => setShowComplete("legacy")}>✓ Submit Review</button>}
          {!isTech && canMarkReviewed && <button className="btn btn-primary btn-sm" onClick={() => setShowClose(true)}>✓ Mark as Reviewed</button>}
          {canClose && !isAborted && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowClose(true)}
                    title="Close this case. This is the final lock - it confirms no manual waveform review is coming and stops QA from being re-run.">
              🔒 Close Case
            </button>
          )}
          {showMenu && (
            <div ref={menuRef} style={{ position:"relative" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setMenuOpen(o => !o)}
                      aria-haspopup="menu" aria-expanded={menuOpen} title="More actions"
                      style={{ fontSize:15, lineHeight:1, padding:"4px 9px", color:"var(--text-tertiary)" }}>
                &#8943;
              </button>
              {menuOpen && (
                <div role="menu" style={{ position:"absolute", top:"calc(100% + 4px)", right:0, zIndex:900,
                     background:"var(--white)", border:"1px solid var(--border)", borderRadius:8,
                     boxShadow:"0 8px 24px rgba(0,0,0,0.12)", minWidth:230, padding:4 }}>
                  {!isTech && (
                    <MenuItem
                      label={qaRunning ? "QA running…" : rerunning ? "Triggering…" : "Re-run QA"}
                      hint={rerunBlockedReason || "Replaces the results and clears the review"}
                      disabled={!!rerunBlockedReason || rerunning || qaRunning}
                      onClick={() => { setMenuOpen(false); handleRerunQA(); }}/>
                  )}
                  {!isTech && hasAuditTypes && techReviewed && (
                    <MenuItem
                      label={unlocking === "Technologist" ? "Unlocking…" : "Unlock Tech Document sign-off"}
                      hint={isClosed ? "Reopen the case first" : "Keeps overrides and notes; that side can be signed again"}
                      disabled={isClosed || !!unlocking}
                      onClick={() => { setMenuOpen(false); handleUnlockSide("Technologist"); }}/>
                  )}
                  {!isTech && hasAuditTypes && ipReviewed && (
                    <MenuItem
                      label={unlocking === "IP" ? "Unlocking…" : "Unlock IP Document sign-off"}
                      hint={isClosed ? "Reopen the case first" : "Keeps overrides and notes; that side can be signed again"}
                      disabled={isClosed || !!unlocking}
                      onClick={() => { setMenuOpen(false); handleUnlockSide("IP"); }}/>
                  )}
                  {!isTech && (
                    <MenuItem
                      label={savingWaveSel ? "Saving…"
                        : onWaveformList ? "Remove from waveform review" : "Add to waveform review"}
                      hint={onWaveformList
                        ? "Takes it off this month's list; the next pick chooses another case for that tech"
                        : "Adds it to this month's list for the waveform reviewer"}
                      disabled={savingWaveSel}
                      onClick={() => { setMenuOpen(false); handleToggleWaveformList(); }}/>
                  )}
                  {!isTech && wfRec?.signed && (
                    <MenuItem
                      label={unlockingWave ? "Reopening…" : "Unlock Waveform sign-off"}
                      hint={isClosed ? "Reopen the case first" : "Keeps the answers; the waveform QA can be signed again"}
                      disabled={isClosed || unlockingWave}
                      onClick={() => { setMenuOpen(false); handleUnlockWaveform(); }}/>
                  )}
                  {isAdmin && !isAborted && (
                    <MenuItem
                      label="Mark aborted"
                      hint="Cancelled case, exclude from scores"
                      onClick={() => { setMenuOpen(false); setShowAbort(true); }}/>
                  )}
                  {isAdmin && isAborted && (
                    <MenuItem
                      label={aborting ? "Restoring…" : "Restore case"}
                      hint="Put it back in the case list and the scores"
                      disabled={aborting}
                      onClick={() => { setMenuOpen(false); doRestore(); }}/>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Complete Review Modal ────────────────────────────── */}
      {showComplete && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"var(--white)", borderRadius:12, padding:28, width:440, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontWeight:600, fontSize:16, marginBottom:6 }}>
              {showComplete === "Technologist" ? "Submit Technologist Review" : showComplete === "IP" ? "Submit IP Review" : "Submit Review"}
            </div>
            <div style={{ fontSize:13, color:"var(--text-secondary)", marginBottom:16 }}>
              {showComplete === "Technologist"
                ? (techFailed.length > 0 ? `All ${techFailed.length} Tech failed checks addressed.` : "All Tech checks passed.")
                : showComplete === "IP"
                ? (ipFailed.length > 0 ? `All ${ipFailed.length} IP failed checks addressed.` : "All IP checks passed.")
                : `All ${failedCount} failed checks addressed.`}
              {" "}This locks the {showComplete === "legacy" ? "document" : `${showComplete === "Technologist" ? "Technologist" : "IP"} section`}.
            </div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", marginBottom:5, textTransform:"uppercase" }}>Summary (optional)</div>
            <textarea style={{ width:"100%", fontSize:12, marginBottom:16, resize:"vertical", minHeight:72, boxSizing:"border-box" }} placeholder="Notes..." value={summary} onChange={e => setSummary(e.target.value)}/>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button className="btn btn-ghost btn-sm" onClick={() => { setShowComplete(null); setSummary(""); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={handleCompleteReview} disabled={completing}>{completing ? "Submitting..." : "Confirm"}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Mark Aborted Modal ───────────────────────────────── */}
      {showAbort && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"var(--white)", borderRadius:12, padding:28, width:440, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontWeight:600, fontSize:16, marginBottom:6 }}>Mark case as aborted</div>
            <div style={{ fontSize:13, color:"var(--text-secondary)", marginBottom:16 }}>
              For a cancelled or abandoned procedure that should not have been QA'd.
              The case leaves the case list and stops counting toward any score.
              Nothing is deleted, and you can restore it from this page.
            </div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", marginBottom:5, textTransform:"uppercase" }}>Reason (optional)</div>
            <textarea value={abortReason} onChange={e => setAbortReason(e.target.value)}
              placeholder="e.g. procedure cancelled, no monitoring performed"
              style={{ width:"100%", minHeight:72, padding:"8px 10px", border:"1px solid var(--border)", borderRadius:6, fontSize:13, resize:"vertical", boxSizing:"border-box" }}/>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
              <button className="btn btn-ghost btn-sm" disabled={aborting}
                onClick={() => { setShowAbort(false); setAbortReason(""); }}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={aborting}
                onClick={() => doAbort(abortReason)}>
                {aborting ? "Marking…" : "Mark aborted"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Close Document Modal ─────────────────────────────── */}
      {showClose && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"var(--white)", borderRadius:12, padding:28, width:420, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontWeight:600, fontSize:16, marginBottom:6 }}>{canClose ? "Close Case" : "Mark as Reviewed"}</div>
            <div style={{ fontSize:13, color:"var(--text-secondary)", marginBottom:16 }}>
              {canClose
                ? "This is the final step. It confirms no manual waveform review is coming, locks the case and stops QA from being re-run. You can reopen it later. Takes a few seconds."
                : "No failed checks. This confirms QA sign-off."}
            </div>
            <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", marginBottom:5, textTransform:"uppercase" }}>Note (optional)</div>
            <textarea value={closeNote} onChange={e => setCloseNote(e.target.value)} placeholder="e.g. All checks passed" style={{ width:"100%", minHeight:72, padding:"8px 10px", border:"1px solid var(--border)", borderRadius:6, fontSize:13, resize:"vertical", boxSizing:"border-box" }}/>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:16 }}>
              <button className="btn btn-ghost btn-sm" disabled={closing} onClick={() => setShowClose(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={closing} onClick={async () => {
                if (closing) return;
                setClosing(true);
                try { await doCloseCase(closeNote); setShowClose(false); setCloseNote(""); }
                catch (e) { alert("Failed: " + (e?.message || String(e))); }
                setClosing(false);
              }}>{closing ? (canClose ? "Closing…" : "Saving…") : (canClose ? "Close Case" : "Mark as Reviewed")}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────── */}
      <div className="page-body" style={{ display:"flex", gap:16, alignItems:"flex-start" }}>
        <div style={{ flex:1, minWidth:0 }}>

          {/* QA re-run banner — tracker-driven, survives refresh */}
          {qaRunning && (
            <>
              <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
              <div className="card" style={{ marginBottom:12, padding:"12px 16px", display:"flex", alignItems:"center", gap:10, borderLeft:"4px solid #3b82f6", background:"#eff6ff" }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#2563eb" strokeWidth="2" style={{ animation:"spin 1s linear infinite" }}>
                  <path d="M8 2a6 6 0 1 0 6 6" strokeLinecap="round"/>
                </svg>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:"#1d4ed8" }}>QA is re-running…</div>
                  <div style={{ fontSize:11.5, color:"var(--text-secondary)" }}>Usually 2–3 minutes. Results refresh automatically — safe to leave and come back.</div>
                </div>
              </div>
            </>
          )}

          {/* Score summary card. Status and Last touched on top, then one row
              of scores: Audit (the two people being audited) and Other reviews. */}
          {qa && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ padding:"16px 20px 18px" }}>
                <div style={{ display:"flex", justifyContent:"space-between",
                              alignItems:"flex-start", gap:16, marginBottom:14 }}>
                  <div>
                    <div style={{ fontWeight:600, fontSize:16, marginBottom:2, letterSpacing:"-0.01em",
                         color: isClosed ? "#16a34a" : undefined }}>
                      {isAborted ? "Aborted"
                        : isDone ? (isClosed ? "Closed" : "Review Complete")
                        : failedCount > 0 ? "QA Failed \u2014 Review Required"
                        : undeterminedCount > 0
                          ? `QA Incomplete \u2014 ${undeterminedCount} check${undeterminedCount === 1 ? "" : "s"} could not be graded`
                          : "QA Passed"}
                    </div>
                    {lastTouched && (
                      <div style={{ fontSize:11.5, fontStyle:"italic", color:"var(--text-tertiary)" }}>
                        Last touched {lastTouched.toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", gap:6, alignItems:"flex-end" }}>
                    <span className="chip">Ruleset {qa.rulesetVersion}</span>
                    <span className="chip">{doc.procedureType || "IONM"}</span>
                  </div>
                </div>

                {hasAuditTypes
                  ? <AuditScoreRow sections={headerSections}/>
                  : qa.categories && (
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"4px 20px" }}>
                      {qa.categories.map(cat => {
                        const cApp = cat.rules.filter(r => r.status !== "NOT_APPLICABLE");
                        const cP = cApp.filter(r => r.effectiveStatus === "PASSED").length;
                        const cT = cApp.length;
                        const pct = cT > 0 ? (cP/cT)*100 : 0;
                        return (
                          <div key={cat.name} className="score-bar-row">
                            <span className="score-bar-label">{cat.name}</span>
                            <div className="score-bar-track"><div className={`score-bar-fill ${pct===100?"full":pct>50?"partial":"zero"}`} style={{width:`${pct}%`}}/></div>
                            <span className="score-bar-val">{cP}/{cT}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
              </div>
            </div>
          )}

          {/* Source Summary Banner — PID_QA only */}
          {isPidQa && availableSources.size > 0 && (
            <SourceSummaryBanner availableSources={availableSources}/>
          )}

          {/* Technologist flags. Sits above the review progress because it is
              the one thing on this page someone is waiting on an answer for. */}
          {flaggedRules.length > 0 && (
            <div className="card" style={{ marginBottom:12, padding:"12px 16px",
              borderLeft:"4px solid #f59e0b", background:"#fffbeb" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                <span style={{ fontSize:14, color:"#b45309" }}>&#9873;</span>
                <div style={{ fontWeight:600, fontSize:13, color:"#b45309" }}>
                  {flaggedRules.length} check{flaggedRules.length === 1 ? "" : "s"} flagged by the technologist
                </div>
                <div style={{ marginLeft:"auto", fontSize:11, fontFamily:"var(--font-mono)", color:"#b45309" }}>
                  {isTech ? "Awaiting a reviewer" : "Open a check below to reply"}
                </div>
              </div>
              {flaggedRules.map(({ rule: r, flag }) => (
                <div key={r.ruleId} style={{ padding:"6px 0 0", borderTop:"1px solid #fde68a", marginTop:6 }}>
                  <div style={{ fontSize:12, color:"var(--text-secondary)", lineHeight:1.5 }}>
                    <span style={{ fontFamily:"var(--font-mono)", fontSize:10.5, color:"#b45309", marginRight:6 }}>
                      {r.ruleId}
                    </span>
                    {r.description}
                  </div>
                  <div style={{ fontSize:12, color:"var(--text-primary)", lineHeight:1.5, marginTop:3 }}>
                    &ldquo;{flag.note}&rdquo;
                  </div>
                  <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--text-tertiary)", marginTop:2 }}>
                    {flag.by || "Unknown"}
                    {flag.at ? ` \u00b7 ${new Date(flag.at).toLocaleString()}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Review progress bar */}
          {!isDone && failedCount > 0 && (
            <div className="card" style={{ marginBottom:12, padding:"12px 16px" }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"var(--text-secondary)" }}>Review progress</span>
                <span style={{ fontSize:12, fontFamily:"var(--font-mono)", color:"#3b82f6" }}>{addressedCount}/{failedCount} addressed</span>
              </div>
              <div style={{ height:4, background:"var(--gray-100, #f3f4f6)", borderRadius:2 }}>
                <div style={{ height:"100%", borderRadius:2, background: allAddressed || (techAllAddressed && ipAllAddressed) ? "#16a34a" : "#3b82f6", width:`${(addressedCount/failedCount)*100}%`, transition:"width 0.3s" }}/>
              </div>
              {hasAuditTypes && (
                <div style={{ display:"flex", gap:16, marginTop:8 }}>
                  {techFailed.length > 0 && (
                    <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color: techReviewed ? "#16a34a" : techAllAddressed ? "#0891b2" : "var(--text-tertiary)" }}>
                      {techReviewed ? "✓ Tech reviewed" : `Tech: ${techAddressed}/${techFailed.length}`}
                    </span>
                  )}
                  {!isTech && ipFailed.length > 0 && (
                    <span style={{ fontSize:11, fontFamily:"var(--font-mono)", color: ipReviewed ? "#16a34a" : ipAllAddressed ? "#7c3aed" : "var(--text-tertiary)" }}>
                      {ipReviewed ? "✓ IP reviewed" : `IP: ${ipAddressed}/${ipFailed.length}`}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Clinical Alerts */}
          {doc.alertDetails && doc.alertDetails.length > 0 && (
            <AlertsSection alerts={doc.alertDetails}/>
          )}

          {/* Tabs + Audit Type Toggle */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:0 }}>
            <div className="tabs" style={{ marginBottom:0 }}>
              <button className={`tab${tab==="qa"?" active":""}`} onClick={() => setTab("qa")}>
                Document {failedCount > 0 ? `(${failedCount} failed)` : ""}
              </button>
              {!isIP && (
              <button className={`tab${tab==="waveform"?" active":""}`} onClick={() => setTab("waveform")}>
                Waveform
                {waveformData?.groups?.some(g => g.alert) && <span style={{ marginLeft:6, background:"#dc2626", color:"#fff", fontFamily:"var(--font-mono)", fontSize:10, padding:"1px 5px", borderRadius:10 }}>{waveformData.groups.filter(g => g.alert).length}</span>}
              </button>
              )}
              {isAdmin && (
                <button className={`tab${tab==="mso"?" active":""}`} onClick={() => setTab("mso")}>
                  MSO
                </button>
              )}
              {isAdmin && (
                <button className={`tab${tab==="idr"?" active":""}`} onClick={() => setTab("idr")}>
                  IDR
                </button>
              )}
            </div>
            {!isTech && !isIP && tab === "qa" && hasAuditTypes && (
              <AuditTypeToggle value={auditType} onChange={setAuditType}/>
            )}
          </div>

          {/* ── QA TAB ─────────────────────────────────────────── */}
          {tab === "qa" && qa && (
            <div>
              {hasAuditTypes ? (
                <>
                  {/* Technologist section */}
                  {techRules.length > 0 && (
                    (!isTech && auditType === "IP") ? (
                      <CollapsedAuditSummary label="Technologist Audit" rules={techRules} onExpand={() => setAuditType("Both")}/>
                    ) : (
                      <AuditTypeSection label="Technologist Audit" rules={techRules}
                        docId={id} reviewerName={reviewerName} isDocReviewed={isDone || techReviewed}
                        onDecisionSaved={handleDecisionSaved} onFlagSaved={handleFlagSaved} onViewWaveform={waveformData ? handleViewWaveform : null}
                        isSideReviewed={techReviewed} sideReviewedBy={isTech ? undefined : qa?.techReviewedBy}
                        canSubmitSide={canSubmitTech || canMarkTech} onSubmitSide={() => setShowComplete("Technologist")}/>
                    )
                  )}
                  {/* IP section */}
                  {!isTech && ipRules.length > 0 && (
                    auditType === "Technologist" ? (
                      <CollapsedAuditSummary label="Interpreting Physician Audit" rules={ipRules} onExpand={() => setAuditType("Both")}/>
                    ) : (
                      <AuditTypeSection label="Interpreting Physician Audit" rules={ipRules}
                        docId={id} reviewerName={reviewerName} isDocReviewed={isDone || ipReviewed}
                        onDecisionSaved={handleDecisionSaved} onFlagSaved={handleFlagSaved} onViewWaveform={waveformData ? handleViewWaveform : null}
                        isSideReviewed={ipReviewed} sideReviewedBy={qa?.ipReviewedBy}
                        canSubmitSide={canSubmitIP || canMarkIP} onSubmitSide={() => setShowComplete("IP")}/>
                    )
                  )}
                </>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  {qa.categories.map(cat => (
                    <div key={cat.name} className="card">
                      <div className="card-header">
                        <div><div className="card-title">{cat.name}</div><div className="card-subtitle">{cat.rules.filter(r=>r.effectiveStatus==="PASSED").length}/{cat.rules.filter(r=>r.status!=="NOT_APPLICABLE").length} passed</div></div>
                        {cat.rules.some(r => r.effectiveStatus === "FAILED") && <span className="badge failed"><span className="badge-dot"/>{cat.rules.filter(r=>r.effectiveStatus==="FAILED").length} failed</span>}
                      </div>
                      <div style={{ padding:"0 18px" }}>
                        {cat.rules.map(r => <QARow key={r.ruleId} rule={r} docId={id} reviewerName={reviewerName} isDocReviewed={isDone} onDecisionSaved={handleDecisionSaved} onFlagSaved={handleFlagSaved} onViewWaveform={waveformData ? handleViewWaveform : null}/>)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Waveform TAB — manual QA ───────────────────────── */}
          {tab === "waveform" && (
            <WaveformQATab caseId={doc?.patientId || id} availableSources={availableSources}
              caseClosed={isClosed}
              canCloseCase={canClose}
              onCloseCase={() => doCloseCase("Closed at waveform sign-off")} />
          )}

          {/* ── MSO TAB ──────────────────────────────────────── */}
          {tab === "mso" && isAdmin && (
            <MSOTab
              caseId={doc?.patientId || id}
              isAdmin={isAdmin}
            />
          )}

          {/* ── IDR TAB ──────────────────────────────────────── */}
          {tab === "idr" && isAdmin && (
            <IDRTab
              caseId={doc?.patientId || id}
              isAdmin={isAdmin}
            />
          )}
        </div>

        {/* ── PDF sidebar ──────────────────────────────────────── */}
        {showPdf && pdfUrl && (
          <div style={{ width:"45%", flexShrink:0, position:"sticky", top:16, height:"calc(100vh - 90px)", display:"flex", flexDirection:"column" }}>
            <div className="card" style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>
              <div className="card-header">
                <div className="card-title">Document Preview</div>
                <div style={{ display:"flex", gap:6 }}>
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-sm">Open ↗</a>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowPdf(false)}>✕</button>
                </div>
              </div>
              {/* Multi-PDF tab bar for PID docs */}
              {siblingPdfs.length > 1 && (
                <div style={{ display:"flex", gap:0, borderBottom:"1px solid var(--border)", background:"var(--gray-50, #f9fafb)", overflowX:"auto" }}>
                  {siblingPdfs.map((sp, idx) => {
                    const active = idx === activePdfIdx;
                    const typeShort = { "Usmon Chat Log":"CHAT", "Interpretation Report":"IP", "Technical Report":"TECH" }[sp.sourceType] || sp.sourceType;
                    return (
                      <button key={sp.filename} onClick={() => { setActivePdfIdx(idx); setPdfUrl(sp.url); }}
                        style={{ padding:"7px 14px", fontSize:11, fontFamily:"var(--font-mono)", cursor:"pointer",
                          border:"none", borderBottom: active ? "2px solid #3b82f6" : "2px solid transparent",
                          background: active ? "var(--white)" : "transparent",
                          color: active ? "#1d4ed8" : "var(--text-tertiary)",
                          fontWeight: active ? 600 : 400, whiteSpace:"nowrap", outline:"none" }}>
                        {typeShort}
                      </button>
                    );
                  })}
                </div>
              )}
              <div style={{ flex:1, overflow:"hidden", borderRadius:"0 0 8px 8px" }}>
                <iframe src={pdfUrl} title="PDF Preview" style={{ width:"100%", height:"100%", border:"none" }}/>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}